import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react';
import Drawer from '@app/components/Drawer';
import DeleteConfirmationModal from '@app/components/DeleteConfirmationModal';
import CMDBDeviceCrosswalk, { type CrosswalkFilter } from '@app/components/cmdb/CMDBDeviceCrosswalk';
import CMDBSetupGuide from '@app/components/cmdb/CMDBSetupGuide';
import CMDBOperatingPolicyEditor, { type CMDBOperatingPolicyForm } from '@app/components/cmdb/CMDBOperatingPolicyEditor';
import ListWorkspace, { type ListWorkspaceColumn } from '@app/components/ListWorkspace';
import StatusToast, { type StatusToastTone } from '@app/components/StatusToast';
import Tabs, { type TabItem } from '@app/components/Tabs';
import CopyField from '@app/components/CopyField';
import { TrashIcon } from '@app/components/icons';
import { useActiveOrgId } from '@app/hooks/useActiveOrg';
import { useAuthFetch } from '@app/hooks/useAuthFetch';
import { useAuditLogApi, useCloudIntegrationsApi, useCMDBReconciliationApi, useIntegrationsApi, useSecretsApi, useSitesApi } from '@app/hooks/useApis';
import { useApiConfiguration } from '@app/hooks/useApiConfiguration';
import type {
  CMDBMaturityScore as APICMDBMaturityScore,
  CMDBDeviceCrosswalkResponse,
  CMDBIdentityHistory,
  CMDBReconciliationPolicy as APICMDBReconciliationPolicy,
  CMDBSourceRecord,
  CMDBSourceRecordHistory,
  CMDBSourceRetentionPolicy,
  IntegrationConnectionCMDBAccessCheckItem,
  IntegrationConnectionCMDBAccessCheckRequest,
  IntegrationConnectionCMDBAccessCheckResponse,
  IntegrationConnection as APIIntegrationConnection,
  IntegrationConnectionsListResponse as APIIntegrationConnectionsListResponse,
  IntegrationConnectionCMDBSourceEvidenceSummary,
  IntegrationConnectionCMDBSyncJob,
  IntegrationConnectionCMDBWritebackPolicy as APICMDBWritebackPolicy,
  IntegrationConnectionCMDBWritebackPolicyUpdateRequest,
  IntegrationWebhookProvisioning as APIIntegrationWebhookProvisioning,
  InteractionRoutingRule as APIInteractionRoutingRule,
  InteractionRoutingRulesListResponse as APIInteractionRoutingRulesListResponse,
  InteractionTarget as APIInteractionTarget,
  InteractionTargetsListResponse as APIInteractionTargetsListResponse,
  AWSCloudWatchLogsIntegration,
  AWSCloudWatchLogsIntegrationCreate,
  AWSCloudWatchLogsIntegrationEnableResponse,
  AWSCloudWatchLogsIntegrationOnboardResponse,
  AWSFlowLogsIntegration,
  AWSFlowLogsIntegrationCreate,
  AWSFlowLogsIntegrationEnableResponse,
  AWSFlowLogsIntegrationOnboardResponse,
  AWSFlowLogsResourceOptionsResponse,
  AuditEvent,
  CloudTrailIntegration,
  CloudTrailIntegrationEnableResponse,
  CloudTrailIntegrationOnboardResponse,
  ProviderDefinitionsListResponse as APIProviderDefinitionsListResponse,
  Site
} from '@app/types/sdk';
import { navigatePendingTab, openPendingTab } from '@app/utils/aws-launch';
import { resolveErrorMessage } from '@app/utils/errors';
import { formatDateTime } from '@app/utils/format';
import { ApiResponseError, buildOrgUrl, cloneHeadersWithAuth, resolveBasePath } from '@app/utils/api';
import {
  buildIntegrationConnectionPayload,
  getIntegrationConfigValue,
  integrationConfigKeys,
  isIntegrationConfigFieldVisible
} from '@app/utils/integrationConnectionConfig';
import { formatConnectionStatusBadge } from '@app/utils/idp';
import OrgModuleLayout from './OrgModuleLayout';
import OrgModulePlaceholder from './OrgModulePlaceholder';
import { collectCursorPages, collectOffsetPages } from './azureResourceChangesPagination';
import { buildProviderBootstrapRequest, type ProviderBootstrapAction } from './providerBootstrapRequest';

type Provider = string;
type FilterProvider = 'all' | string;

type IntegrationConnection = {
  id?: string;
  orgId?: string;
  name?: string;
  provider?: string;
  status?: string;
  description?: string | null;
  config?: {
    instanceUrl?: string;
    siteUrl?: string;
    clientId?: string;
    clientSecretId?: string;
    keyId?: string;
    workspaceId?: string;
    enterpriseId?: string;
    tenantId?: string;
    teamId?: string;
    appId?: string;
    botUserId?: string;
    manifestAppId?: string;
    personalTabEntityId?: string;
    signingSecretId?: string;
    accessTokenSecretId?: string;
    cloudId?: string;
    userEmail?: string;
    mode?: string;
    baseUrl?: string;
    portalName?: string;
    customerId?: string;
    username?: string;
    passwordSecretId?: string;
    tokenUrl?: string;
    grantCodeSecretId?: string;
    refreshTokenSecretId?: string;
    apiKeySecretId?: string;
    additional?: Record<string, unknown>;
    webhook?: {
      registrationId?: string;
      status?: string;
      providerWebhookId?: string;
    };
  };
  lastTestedAt?: string;
  lastError?: string | null;
  lastErrorCode?: string | null;
  createdAt?: string;
  updatedAt?: string;
  setupRequest?: IntegrationSetupRequest;
};

type IntegrationSetupRequest = {
  id: string;
  provider: string;
  status?: string;
  connectionId?: string | null;
  lastError?: string | null;
  expiresAt?: string;
};

type ProviderDefinitionField = {
  fieldKey: string;
  label: string;
  inputType: string;
  required?: boolean;
  secret?: boolean;
  description?: string;
  placeholder?: string;
  section?: string;
  fieldOrder?: number;
  attributes?: Record<string, unknown>;
};

type ProviderDefinition = {
  id: string;
  providerKey: string;
  displayName: string;
  category: string;
  connectionType: string;
  description?: string;
  enabled?: boolean;
  supportsTest?: boolean;
  supportsTargets?: boolean;
  supportsRouting?: boolean;
  supportsResolve?: boolean;
  supportsWebhookSetup?: boolean;
  supportsCMDBSync?: boolean;
  supportsCMDBWriteback?: boolean;
  version?: string;
  source?: string;
  bootstrap?: ProviderBootstrapAction | null;
  fields?: ProviderDefinitionField[];
};

type IntegrationWebhookProvisioning = {
  provider: string;
  registrationId: string;
  url: string;
  method?: string;
  signingSecret: string;
  secretHeader?: string;
  signatureHeader?: string;
  timestampHeader?: string;
  setupScript?: string;
  setupScriptLabel?: string;
  serviceNowScript?: string;
  setupInstructions?: string;
  status?: string;
};

type CMDBWritebackHistoryEntry = {
  changedAt?: string;
  changedBy?: string;
  updateExisting?: boolean;
  createMissing?: boolean;
};

type CMDBWritebackPolicy = {
  updateExisting: boolean;
  createMissing: boolean;
  mode: 'report_only' | 'approval_required' | 'controlled_writeback';
  publicationEnabled: boolean;
  globalPublicationEnabled: boolean;
  tenantPublicationEnabled: boolean;
  executionEnabled: boolean;
  allowedOperations: string[];
  allowedFields: string[];
  supportedOperations: string[];
  supportedFields: Array<{ key: string; label: string }>;
  createMissingSupported: boolean;
  updatedAt?: string;
  updatedBy?: string;
  history: CMDBWritebackHistoryEntry[];
};

type InteractionTarget = {
  id?: string;
  orgId?: string;
  name?: string;
  provider?: string;
  connectionId?: string;
  targetType?: string;
  externalTargetId?: string;
  threadBindingId?: string;
  isDefault?: boolean;
  labels?: string[];
  meta?: Record<string, unknown>;
  createdAt?: string;
  updatedAt?: string;
};

type InteractionRoutingRule = {
  id?: string;
  orgId?: string;
  name?: string;
  provider?: string;
  connectionId?: string;
  targetRef?: string;
  assignmentGroup?: string;
  category?: string;
  priority?: string;
  conditions?: Record<string, unknown>;
  targetOverrideRef?: string;
  enabled?: boolean;
  createdAt?: string;
  updatedAt?: string;
};

type CloudProvider = 'aws' | 'azure';
type CloudSignal = 'cloudtrail' | 'flowlogs' | 'cloudwatch-logs' | 'resource-changes';
type AWSCloudLogIntegration = CloudTrailIntegration | AWSFlowLogsIntegration | AWSCloudWatchLogsIntegration;

type CloudTrailSiteRow = {
  siteId: string;
  siteName: string;
  awsAccountId: string;
  region: string;
  integration: AWSCloudLogIntegration | null;
  status: 'enabled' | 'disabled' | 'action_required';
  vpcIds: string[];
  transitGatewayIds: string[];
  logGroups: string[];
  resourceSnapshotAt?: string;
  updatedAt?: Date;
};

type DeleteDialogState =
  | {
      kind: 'connection';
      connection: IntegrationConnection;
      title: string;
      description: string;
      successMessage: string;
    }
  | {
      kind: 'cloud';
      row: CloudTrailSiteRow;
      title: string;
      description: string;
      successMessage: string;
    }
  | {
      kind: 'target';
      target: InteractionTarget;
      title: string;
      description: string;
      successMessage: string;
    }
  | {
      kind: 'rule';
      rule: InteractionRoutingRule;
      title: string;
      description: string;
      successMessage: string;
    };

type DeleteToastState = {
  tone: StatusToastTone;
  message: string;
};

type AWSFlowLogsLaunchResponse = (AWSFlowLogsIntegrationOnboardResponse | AWSFlowLogsIntegrationEnableResponse) & {
  quickCreateUrl?: string;
};

type AWSCloudWatchLogsAPIKeyResponse = {
  apiKey?: string;
};

type AWSCloudWatchLogsLaunchResponse = (AWSCloudWatchLogsIntegrationOnboardResponse | AWSCloudWatchLogsIntegrationEnableResponse) & AWSCloudWatchLogsAPIKeyResponse;

type AWSCloudLaunchResponse = (CloudTrailIntegrationOnboardResponse | CloudTrailIntegrationEnableResponse | AWSFlowLogsLaunchResponse | AWSCloudWatchLogsLaunchResponse) & {
  quickCreateUrl?: string;
} & AWSCloudWatchLogsAPIKeyResponse;

type AWSFlowLogsResourceOption = AWSFlowLogsResourceOptionsResponse['vpcs'][number];

type AwsCloudSite = Site & {
  awsAccountId?: string;
};

type AzureCloudSite = Site & { azureSubscriptionId?: string; subscriptionId?: string };
type AzureResourceChangesIntegration = { id?: string; name?: string; subscriptionId?: string; status?: 'enabled' | 'disabled'; createdAt?: string; updatedAt?: string };
type AzureResourceChangesRow = { siteId: string; siteName: string; subscriptionId: string; integration: AzureResourceChangesIntegration | null };

type ConnectionFormState = {
  provider: string;
  name: string;
  values: Record<string, string>;
};

type CMDBWritebackFormState = CMDBOperatingPolicyForm;

type CMDBReconciliationPolicy = {
  explicitMappingRequired: boolean;
  manualMaturityLevel?: 'well_maintained' | 'hybrid' | 'not_maintained';
  manualMaturityScore?: number;
  updatedAt?: string;
  updatedBy?: string;
};

type CMDBReconciliationFormState = {
  explicitMappingRequired: boolean;
  manualMaturityLevel: '' | NonNullable<CMDBReconciliationPolicy['manualMaturityLevel']>;
  manualMaturityScore: string;
};

type CMDBMaturityScore = {
  connectionId?: string;
  provider?: string;
  score: number;
  level: 'well_maintained' | 'hybrid' | 'not_maintained';
  source: 'observed' | 'manual_override';
  generatedAt?: string;
  bindingCoveragePercent?: number;
  freshnessPercent?: number;
  driftRatePercent?: number;
  ambiguityRatePercent?: number;
  orphanRatePercent?: number;
  relationUsabilityPercent?: number;
};

type ConnectionDrawerTab = 'overview' | 'cmdb' | 'webhook';
type CMDBWorkspaceTab = 'access' | 'sync' | 'matching' | 'interpretation' | 'governance';

type TargetFormState = {
  name: string;
  provider: Provider;
  connectionId: string;
  targetType: string;
  externalTargetId: string;
  threadBindingId: string;
  isDefault: boolean;
  labels: string;
  metaJson: string;
};

type RuleFormState = {
  name: string;
  provider: Provider;
  connectionId: string;
  targetRef: string;
  assignmentGroup: string;
  category: string;
  priority: string;
  targetOverrideRef: string;
  enabled: boolean;
  conditionsJson: string;
};

type FilterState = {
  status: 'all' | 'active' | 'pending' | 'error' | 'disabled';
  provider: FilterProvider;
  q: string;
};

const PAGE_SIZE_OPTIONS = [10, 20, 50];
const CMDB_ACCESS_FEEDBACK_TIMEOUT_MS = 4000;
const CMDB_POLICY_HISTORY_PREVIEW_COUNT = 5;
const CMDB_WRITEBACK_POLICY_AUDIT_EVENT = 'audit.policy.update';
const CMDB_SOURCE_DOMAIN_LABELS: Record<string, string> = {
  cis: 'Configuration items',
  relations: 'Relationships and types',
  incidents: 'Incidents and CI links',
  endpoints: 'Service endpoints',
  serviceLinks: 'Service-to-endpoint links'
};
const formatAuditActor = (event?: AuditEvent | null) =>
  event?.actor.displayName?.trim() || event?.actor.email?.trim() || event?.actor.id?.trim() || (event ? 'System' : '—');

const formatPolicyAuditSummary = (event: AuditEvent) => {
  const after = event.metadata?.after as Record<string, unknown> | undefined;
  if (!after) {
    return 'Policy updated';
  }
  const mode = typeof after.mode === 'string' ? after.mode.replaceAll('_', ' ') : 'unknown mode';
  return `${mode} · update matched CIs: ${after.updateExisting === true ? 'on' : 'off'} · create missing CIs: ${after.createMissing === true ? 'on' : 'off'}`;
};

const formatReconciliationAuditSummary = (event: AuditEvent) => {
  const after = event.metadata?.after as Record<string, unknown> | undefined;
  if (!after) return 'Reconciliation settings updated';
  const maturity = typeof after.manualMaturityLevel === 'string' ? `legacy maturity: ${after.manualMaturityLevel.replaceAll('_', ' ')}` : '';
  return maturity || 'Reconciliation settings updated';
};

const cloudProviderOptions: Array<{ value: CloudProvider; label: string; description: string }> = [
  { value: 'aws', label: 'AWS', description: 'Select an AWS integration source.' },
  { value: 'azure', label: 'Azure', description: 'Select an Azure integration source.' }
];

const cloudSignalOptions: Record<CloudProvider, Array<{ value: CloudSignal; label: string; description: string }>> = {
  aws: [
    {
      value: 'cloudtrail',
      label: 'CloudTrail',
      description: 'Receive CloudTrail events in REAP.'
    },
    {
      value: 'flowlogs',
      label: 'Flow Logs',
      description: 'Receive VPC and transit gateway flow logs in REAP.'
    },
    {
      value: 'cloudwatch-logs',
      label: 'CloudWatch Logs',
      description: 'Receive selected CloudWatch log groups in REAP.'
    }
  ],
  azure: [{ value: 'resource-changes', label: 'Resource changes', description: 'Receive Azure Event Grid resource changes by subscription.' }]
};

const defaultConnectionForm = (provider = '', name = ''): ConnectionFormState => ({
  provider,
  name,
  values: {}
});

const defaultCMDBWritebackForm = (): CMDBWritebackFormState => ({
  updateExisting: false,
  createMissing: false,
  mode: 'report_only',
  publicationEnabled: false,
  allowedFields: []
});

const defaultCMDBWritebackPolicy = (): CMDBWritebackPolicy => ({
  updateExisting: false,
  createMissing: false,
  mode: 'report_only',
  publicationEnabled: false,
  globalPublicationEnabled: false,
  tenantPublicationEnabled: false,
  executionEnabled: false,
  allowedOperations: [],
  allowedFields: [],
  supportedOperations: [],
  supportedFields: [],
  createMissingSupported: false,
  history: []
});

const defaultCMDBReconciliationPolicy = (): CMDBReconciliationPolicy => ({
  explicitMappingRequired: true,
});

const defaultCMDBReconciliationForm = (): CMDBReconciliationFormState => ({
  explicitMappingRequired: true,
  manualMaturityLevel: '',
  manualMaturityScore: '',
});

const defaultTargetForm = (provider = ''): TargetFormState => ({
  name: '',
  provider,
  connectionId: '',
  targetType: 'channel',
  externalTargetId: '',
  threadBindingId: '',
  isDefault: false,
  labels: '',
  metaJson: '{}'
});

const defaultRuleForm = (provider = ''): RuleFormState => ({
  name: '',
  provider,
  connectionId: '',
  targetRef: '',
  assignmentGroup: '',
  category: '',
  priority: '',
  targetOverrideRef: '',
  enabled: true,
  conditionsJson: '{}'
});

const providerCategoryTicketing = 'ticketing';
const providerCategoryInteraction = 'interaction';
const normalizeProviderCategory = (value?: string | null): string => (value ?? '').trim().toLowerCase();
const isTicketingDefinition = (definition?: ProviderDefinition | null) => normalizeProviderCategory(definition?.category) === providerCategoryTicketing;
const isInteractionDefinition = (definition?: ProviderDefinition | null) =>
  normalizeProviderCategory(definition?.category) === providerCategoryInteraction || Boolean(definition?.supportsTargets || definition?.supportsRouting);
const supportsCMDBSync = (definition?: ProviderDefinition | null) => Boolean(definition?.supportsCMDBSync);
const supportsCMDBWriteback = (definition?: ProviderDefinition | null) => Boolean(definition?.supportsCMDBWriteback);
const supportsConnectionTest = (definition?: ProviderDefinition | null) => Boolean(definition?.supportsTest);
const supportsWebhookSetup = (definition?: ProviderDefinition | null) => Boolean(definition?.supportsWebhookSetup);

const providerDescription = (definition?: ProviderDefinition | null) => definition?.description ?? 'Provider-managed integration connection.';

const mapPersistedCMDBAccessCheck = (raw: unknown): IntegrationConnectionCMDBAccessCheckResponse | null => {
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  const mapped = raw as Record<string, unknown>;
  const policyRaw = mapped.policy;
  if (!policyRaw || typeof policyRaw !== 'object') {
    return null;
  }
  const policy = policyRaw as Record<string, unknown>;
  const items: IntegrationConnectionCMDBAccessCheckItem[] = Array.isArray(mapped.items)
    ? mapped.items.flatMap((entry) => {
        if (!entry || typeof entry !== 'object') {
          return [];
        }
        const item = entry as Record<string, unknown>;
        const scope = item.scope;
        const status = item.status;
        if ((scope !== 'read' && scope !== 'write') || (status !== 'pass' && status !== 'fail' && status !== 'disabled')) {
          return [];
        }
        return [
          {
            scope,
            table: typeof item.table === 'string' ? item.table : 'unknown',
            operation: typeof item.operation === 'string' ? item.operation : 'read',
            enabled: item.enabled !== false,
            status,
            impact: typeof item.impact === 'string' ? item.impact : undefined,
            message: typeof item.message === 'string' ? item.message : undefined
          }
        ];
      })
    : [];
  const checkedAt = typeof mapped.checkedAt === 'string' || mapped.checkedAt instanceof Date ? new Date(mapped.checkedAt) : null;
  if (!checkedAt || Number.isNaN(checkedAt.getTime())) {
    return null;
  }
  const readStatus = mapped.readStatus;
  const writeStatus = mapped.writeStatus;
  if (
    (readStatus !== 'pass' && readStatus !== 'partial' && readStatus !== 'fail' && readStatus !== 'disabled') ||
    (writeStatus !== 'pass' && writeStatus !== 'partial' && writeStatus !== 'fail' && writeStatus !== 'disabled')
  ) {
    return null;
  }
  return {
    provider: typeof mapped.provider === 'string' ? mapped.provider : '',
    connectionId: typeof mapped.connectionId === 'string' ? mapped.connectionId : '',
    checkedAt,
    readStatus,
    writeStatus,
    readPassedCount: typeof mapped.readPassedCount === 'number' ? mapped.readPassedCount : items.filter((item) => item.scope === 'read' && item.status === 'pass').length,
    readRequiredCount: typeof mapped.readRequiredCount === 'number' ? mapped.readRequiredCount : items.filter((item) => item.scope === 'read' && item.enabled).length,
    writePassedCount: typeof mapped.writePassedCount === 'number' ? mapped.writePassedCount : items.filter((item) => item.scope === 'write' && item.status === 'pass').length,
    writeRequiredCount: typeof mapped.writeRequiredCount === 'number' ? mapped.writeRequiredCount : items.filter((item) => item.scope === 'write' && item.enabled).length,
    issueCount: typeof mapped.issueCount === 'number' ? mapped.issueCount : items.filter((item) => item.status === 'fail').length,
    forceWriteValidation: mapped.forceWriteValidation === true,
    policy: {
      updateExisting: policy.updateExisting === true,
      createMissing: policy.createMissing === true
    },
    items
  };
};

const getPersistedCMDBAccessCheck = (connection: IntegrationConnection | null | undefined): IntegrationConnectionCMDBAccessCheckResponse | null => {
  const config = connection?.config as Record<string, unknown> | undefined;
  const cmdbAccess =
    (config?.cmdbAccess as Record<string, unknown> | undefined) ??
    (connection?.config?.additional?.cmdbAccess as Record<string, unknown> | undefined);
  if (!cmdbAccess) {
    return null;
  }
  return mapPersistedCMDBAccessCheck(cmdbAccess.lastResult);
};

const formatCMDBAccessStatus = (status?: string | null) => {
  switch (status) {
    case 'pass':
      return { label: 'Pass', className: 'ui-badge ui-badge--success' };
    case 'partial':
      return { label: 'Partial', className: 'ui-badge' };
    case 'fail':
      return { label: 'Fail', className: 'ui-badge ui-badge--danger' };
    case 'disabled':
      return { label: 'Disabled', className: 'ui-badge' };
    default:
      return { label: 'Not checked', className: 'ui-badge' };
  }
};

const mapCMDBWritebackPolicy = (policy: APICMDBWritebackPolicy | null | undefined): CMDBWritebackPolicy => {
  if (!policy) {
    return defaultCMDBWritebackPolicy();
  }
  return {
    updateExisting: policy.updateExisting,
    createMissing: policy.createMissing,
    mode: policy.mode,
    publicationEnabled: policy.publicationEnabled,
    globalPublicationEnabled: policy.globalPublicationEnabled === true,
    tenantPublicationEnabled: policy.tenantPublicationEnabled === true,
    executionEnabled: policy.executionEnabled === true,
    allowedOperations: [...(policy.allowedOperations ?? [])],
    allowedFields: [...(policy.allowedFields ?? [])],
    supportedOperations: [...(policy.supportedOperations ?? [])],
    supportedFields: [...(policy.supportedFields ?? [])],
    createMissingSupported: policy.createMissingSupported === true,
    updatedAt: policy.updatedAt instanceof Date ? policy.updatedAt.toISOString() : undefined,
    updatedBy: policy.updatedBy,
    history: (policy.history ?? []).map((entry) => ({
      changedAt: entry.changedAt instanceof Date ? entry.changedAt.toISOString() : undefined,
      changedBy: entry.changedBy,
      updateExisting: entry.updateExisting,
      createMissing: entry.createMissing
    }))
  };
};

const normalizeCMDBDate = (value: unknown): string | undefined => {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? undefined : value.toISOString();
  }
  return typeof value === 'string' && value.trim().length > 0 ? value : undefined;
};

const mapIntegrationConnectionRecord = (record: APIIntegrationConnection): IntegrationConnection => ({
  ...record,
  config: record.config
    ? {
        ...record.config,
        additional: record.config.additional ? { ...record.config.additional } : undefined,
        webhook: record.config.webhook ? { ...record.config.webhook } : undefined
      }
    : undefined,
  description: record.description ?? null,
  lastTestedAt: normalizeCMDBDate(record.lastTestedAt),
  lastError: record.lastError ?? null,
  lastErrorCode: record.lastErrorCode ?? null,
  createdAt: normalizeCMDBDate(record.createdAt),
  updatedAt: normalizeCMDBDate(record.updatedAt)
});

const mapInteractionTargetRecord = (record: APIInteractionTarget): InteractionTarget => ({
  ...record,
  labels: record.labels ? [...record.labels] : undefined,
  meta: record.meta ? { ...record.meta } : undefined,
  createdAt: normalizeCMDBDate(record.createdAt),
  updatedAt: normalizeCMDBDate(record.updatedAt)
});

const mapInteractionRoutingRuleRecord = (record: APIInteractionRoutingRule): InteractionRoutingRule => ({
  ...record,
  conditions: record.conditions ? { ...record.conditions } : undefined,
  createdAt: normalizeCMDBDate(record.createdAt),
  updatedAt: normalizeCMDBDate(record.updatedAt)
});

const mapIntegrationWebhookProvisioning = (
  record: APIIntegrationWebhookProvisioning
): IntegrationWebhookProvisioning => ({
  ...record
});

const mapCMDBReconciliationPolicy = (policy: APICMDBReconciliationPolicy | null | undefined): CMDBReconciliationPolicy => {
  if (!policy) {
    return defaultCMDBReconciliationPolicy();
  }
  return {
    explicitMappingRequired: policy.explicitMappingRequired !== false,
    manualMaturityLevel: (policy.manualMaturityLevel ?? undefined) as CMDBReconciliationPolicy['manualMaturityLevel'],
    manualMaturityScore: typeof policy.manualMaturityScore === 'number' ? policy.manualMaturityScore : undefined,
    updatedAt: normalizeCMDBDate(policy.updatedAt),
    updatedBy: policy.updatedBy ?? undefined
  };
};

const policyToCMDBReconciliationForm = (policy: CMDBReconciliationPolicy): CMDBReconciliationFormState => ({
  explicitMappingRequired: policy.explicitMappingRequired,
  manualMaturityLevel: policy.manualMaturityLevel ?? '',
  manualMaturityScore: typeof policy.manualMaturityScore === 'number' ? String(policy.manualMaturityScore) : '',
});

const mapCMDBMaturityScore = (score: APICMDBMaturityScore | null | undefined): CMDBMaturityScore | null => {
  if (!score) {
    return null;
  }
  return {
    connectionId: score.connectionId ?? undefined,
    provider: score.provider ?? undefined,
    score: score.score ?? 0,
    level: (score.level ?? 'not_maintained') as CMDBMaturityScore['level'],
    source: (score.source ?? 'observed') as CMDBMaturityScore['source'],
    generatedAt: normalizeCMDBDate(score.generatedAt),
    bindingCoveragePercent: score.bindingCoveragePercent ?? undefined,
    freshnessPercent: score.freshnessPercent ?? undefined,
    driftRatePercent: score.driftRatePercent ?? undefined,
    ambiguityRatePercent: score.ambiguityRatePercent ?? undefined,
    orphanRatePercent: score.orphanRatePercent ?? undefined,
    relationUsabilityPercent: score.relationUsabilityPercent ?? undefined
  };
};

const formatCMDBMaturityLabel = (value?: string | null): string => {
  switch ((value ?? '').trim()) {
    case 'well_maintained':
      return 'Well maintained';
    case 'hybrid':
      return 'Hybrid';
    default:
      return 'Not maintained';
  }
};

const connectionFormFromRecord = (record: IntegrationConnection | null, definition?: ProviderDefinition | null): ConnectionFormState => {
  const provider = normalizeProvider(record?.provider) ?? '';
  const values: Record<string, string> = {};
  if (record?.config) {
    const secretFieldKeys = new Set((definition?.fields ?? []).filter((field) => field.secret).map((field) => field.fieldKey));
    for (const key of integrationConfigKeys) {
      if (secretFieldKeys.has(key)) {
        continue;
      }
      const value = getIntegrationConfigValue(record.config, key);
      if (value) {
        values[key] = value;
      }
    }
    for (const [key, value] of Object.entries(record.config.additional ?? {})) {
      if (typeof value === 'string' && !secretFieldKeys.has(key)) {
        values[key] = value;
      }
    }
  }
  return {
    provider,
    name: record?.name ?? definition?.displayName ?? provider,
    values
  };
};

const targetFormFromRecord = (record: InteractionTarget | null, fallbackProvider = ''): TargetFormState => {
  const provider = normalizeProvider(record?.provider) ?? fallbackProvider;
  return {
    name: record?.name ?? '',
    provider,
    connectionId: record?.connectionId ?? '',
    targetType: record?.targetType ?? 'channel',
    externalTargetId: record?.externalTargetId ?? '',
    threadBindingId: record?.threadBindingId ?? '',
    isDefault: Boolean(record?.isDefault),
    labels: (record?.labels ?? []).join(', '),
    metaJson: safeJsonStringify(record?.meta ?? {})
  };
};

const ruleFormFromRecord = (record: InteractionRoutingRule | null, fallbackProvider = ''): RuleFormState => {
  const provider = normalizeProvider(record?.provider) ?? fallbackProvider;
  return {
    name: record?.name ?? '',
    provider,
    connectionId: record?.connectionId ?? '',
    targetRef: record?.targetRef ?? '',
    assignmentGroup: record?.assignmentGroup ?? '',
    category: record?.category ?? '',
    priority: record?.priority ?? '',
    targetOverrideRef: record?.targetOverrideRef ?? '',
    enabled: record?.enabled ?? true,
    conditionsJson: safeJsonStringify(record?.conditions ?? {})
  };
};

const normalizeProvider = (value?: string | null) => {
  const normalized = value?.trim().toLowerCase();
  return normalized || null;
};

const safeJsonStringify = (value: unknown) => {
  try {
    return JSON.stringify(value ?? {}, null, 2);
  } catch {
    return '{}';
  }
};

const parseJsonInput = (label: string, raw: string): Record<string, unknown> => {
  const trimmed = raw.trim();
  if (!trimmed) {
    return {};
  }
  const parsed = JSON.parse(trimmed) as unknown;
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    throw new Error(`${label} must be a JSON object.`);
  }
  return parsed as Record<string, unknown>;
};

const splitCsv = (raw: string) =>
  raw
    .split(',')
    .map((value) => value.trim())
    .filter(Boolean);

const flowLogsSnapshot = (integration: AWSCloudLogIntegration | null) => {
  const item = integration as
    | (AWSFlowLogsIntegration & {
        vpcIds?: string[];
        transitGatewayIds?: string[];
        resourceSnapshotAt?: string;
      })
    | null;
  return {
    vpcIds: Array.isArray(item?.vpcIds) ? item.vpcIds.filter(Boolean) : [],
    transitGatewayIds: Array.isArray(item?.transitGatewayIds) ? item.transitGatewayIds.filter(Boolean) : [],
    resourceSnapshotAt: item?.resourceSnapshotAt
  };
};

const cloudWatchLogGroups = (integration: AWSCloudLogIntegration | null) => {
  const item = integration as
    | (AWSCloudWatchLogsIntegration & {
        logGroups?: string[];
      })
    | null;
  return {
    logGroups: Array.isArray(item?.logGroups) ? item.logGroups.filter(Boolean) : []
  };
};

const parseLogGroupInput = (raw: string) =>
  raw
    .split(/\r?\n/)
    .map((value) => value.trim())
    .filter(Boolean);

const listResponseItems = <T,>(items?: T[] | null): T[] => (Array.isArray(items) ? items : []);

const hasProviderBootstrapAction = (bootstrap?: ProviderBootstrapAction | null) =>
  Boolean(bootstrap?.method?.trim() && bootstrap?.path?.trim());

const supportsProviderBootstrap = (definition?: ProviderDefinition | null) =>
  hasProviderBootstrapAction(definition?.bootstrap);

const integrationSetupIsPending = (setup?: IntegrationSetupRequest | null) =>
  setup?.status === 'pending_approval' || setup?.status === 'oauth_in_progress';

const IntegrationsPage = () => {
  const orgId = useActiveOrgId();
  const apiConfiguration = useApiConfiguration();
  const basePath = useMemo(() => resolveBasePath(apiConfiguration.basePath ?? ''), [apiConfiguration.basePath]);
  const { fetchWithAuthRetry } = useAuthFetch();
  const integrationsApi = useIntegrationsApi();
  const auditLogApi = useAuditLogApi();
  const cloudIntegrationsApi = useCloudIntegrationsApi();
  const secretsApi = useSecretsApi();
  const reconciliationApi = useCMDBReconciliationApi();
  const sitesApi = useSitesApi();

  const [providerDefinitions, setProviderDefinitions] = useState<ProviderDefinition[]>([]);
  const [connectionsTotal, setConnectionsTotal] = useState(0);
  const [connections, setConnections] = useState<IntegrationConnection[]>([]);
  const [targets, setTargets] = useState<InteractionTarget[]>([]);
  const [routingRules, setRoutingRules] = useState<InteractionRoutingRule[]>([]);
  const [highlightRowId, setHighlightRowId] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [integrationStatusMessage, setIntegrationStatusMessage] = useState<string | null>(null);
  const [integrationSetups, setIntegrationSetups] = useState<Record<string, IntegrationSetupRequest>>({});
  const [downloadingTeamsPackageConnectionID, setDownloadingTeamsPackageConnectionID] = useState<string | null>(null);
  const [loadingConnections, setLoadingConnections] = useState(false);
  const [startingBootstrapProviderKey, setStartingBootstrapProviderKey] = useState<string | null>(null);
  const [cancellingSetupProviderKey, setCancellingSetupProviderKey] = useState<string | null>(null);
  const [loadingTargets, setLoadingTargets] = useState(false);
  const [loadingRules, setLoadingRules] = useState(false);
  const [savingConnection, setSavingConnection] = useState(false);
  const [savingTarget, setSavingTarget] = useState(false);
  const [savingRule, setSavingRule] = useState(false);
  const [rowActionBusy, setRowActionBusy] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);
  const [filters, setFilters] = useState<FilterState>({
    status: 'all',
    provider: 'all',
    q: ''
  });

  const [connectionDrawerOpen, setConnectionDrawerOpen] = useState(false);
  const [connectionDrawerMode, setConnectionDrawerMode] = useState<'create' | 'detail'>('create');
  const [connectionDrawerTab, setConnectionDrawerTab] = useState<ConnectionDrawerTab>('overview');
  const [cmdbWorkspaceTab, setCMDBWorkspaceTab] = useState<CMDBWorkspaceTab>('access');
  const [connectionTestFeedback, setConnectionTestFeedback] = useState<{ tone: 'success' | 'error'; message: string } | null>(null);
  const [connectionRecord, setConnectionRecord] = useState<IntegrationConnection | null>(null);
  const [connectionForm, setConnectionForm] = useState<ConnectionFormState>(defaultConnectionForm);
  const [cmdbWritebackPolicy, setCMDBWritebackPolicy] = useState<CMDBWritebackPolicy>(defaultCMDBWritebackPolicy);
  const [cmdbWritebackForm, setCMDBWritebackForm] = useState<CMDBWritebackFormState>(defaultCMDBWritebackForm);
  const [cmdbReconciliationPolicy, setCMDBReconciliationPolicy] = useState<CMDBReconciliationPolicy>(defaultCMDBReconciliationPolicy);
  const [cmdbReconciliationForm, setCMDBReconciliationForm] = useState<CMDBReconciliationFormState>(defaultCMDBReconciliationForm);
  const [cmdbMaturityScore, setCMDBMaturityScore] = useState<CMDBMaturityScore | null>(null);
  const [cmdbSourceEvidence, setCMDBSourceEvidence] = useState<IntegrationConnectionCMDBSourceEvidenceSummary | null>(null);
  const [loadingCMDBSourceEvidence, setLoadingCMDBSourceEvidence] = useState(false);
  const [cmdbSyncJob, setCMDBSyncJob] = useState<IntegrationConnectionCMDBSyncJob | null>(null);
  const [cmdbSyncBusy, setCMDBSyncBusy] = useState(false);
  const [cmdbSourceRecords, setCMDBSourceRecords] = useState<CMDBSourceRecord[]>([]);
  const [cmdbSourceRetention, setCMDBSourceRetention] = useState<CMDBSourceRetentionPolicy | null>(null);
  const [cmdbSourceRecordHistory, setCMDBSourceRecordHistory] = useState<CMDBSourceRecordHistory | null>(null);
  const [cmdbSourceIdentityHistory, setCMDBSourceIdentityHistory] = useState<CMDBIdentityHistory | null>(null);
  const [cmdbDeviceCrosswalk, setCMDBDeviceCrosswalk] = useState<CMDBDeviceCrosswalkResponse | null>(null);
  const [cmdbCrosswalkFilter, setCMDBCrosswalkFilter] = useState<CrosswalkFilter>('all');
  const [loadingCMDBDeviceCrosswalk, setLoadingCMDBDeviceCrosswalk] = useState(false);
  const [cmdbAccessCheckResult, setCMDBAccessCheckResult] = useState<IntegrationConnectionCMDBAccessCheckResponse | null>(null);
  const [cmdbAccessCheckBusy, setCMDBAccessCheckBusy] = useState(false);
  const [loadingCMDBWritebackPolicy, setLoadingCMDBWritebackPolicy] = useState(false);
  const [cmdbPolicyAuditEvents, setCMDBPolicyAuditEvents] = useState<AuditEvent[]>([]);
  const [cmdbPolicyAuditError, setCMDBPolicyAuditError] = useState<string | null>(null);
  const [loadingCMDBPolicyAudit, setLoadingCMDBPolicyAudit] = useState(false);
  const [cmdbReconciliationAuditEvents, setCMDBReconciliationAuditEvents] = useState<AuditEvent[]>([]);
  const [cmdbReconciliationAuditError, setCMDBReconciliationAuditError] = useState<string | null>(null);
  const [loadingCMDBReconciliationAudit, setLoadingCMDBReconciliationAudit] = useState(false);
  const [loadingCMDBReconciliationPolicy, setLoadingCMDBReconciliationPolicy] = useState(false);
  const [webhookProvisioning, setWebhookProvisioning] = useState<IntegrationWebhookProvisioning | null>(null);
  const [webhookLoading, setWebhookLoading] = useState(false);
  const [webhookError, setWebhookError] = useState<string | null>(null);
  const [showWebhookSetup, setShowWebhookSetup] = useState(false);
  const [revealWebhookSecret, setRevealWebhookSecret] = useState(false);
  const [copyFeedback, setCopyFeedback] = useState<string | null>(null);
  const [cloudTrailItems, setCloudTrailItems] = useState<CloudTrailSiteRow[]>([]);
  const [cloudTrailLoading, setCloudTrailLoading] = useState(false);
  const [cloudTrailErrorMessage, setCloudTrailErrorMessage] = useState<string | null>(null);
  const [cloudTrailActionBusy, setCloudTrailActionBusy] = useState<string | null>(null);
  const [cloudTrailDrawerOpen, setCloudTrailDrawerOpen] = useState(false);
  const [cloudTrailDrawerRecord, setCloudTrailDrawerRecord] = useState<CloudTrailSiteRow | null>(null);
  const [awsQuickCreateURL, setAwsQuickCreateURL] = useState<string | null>(null);
  const [flowLogsPickerOpen, setFlowLogsPickerOpen] = useState(false);
  const [flowLogsPickerRow, setFlowLogsPickerRow] = useState<CloudTrailSiteRow | null>(null);
  const [flowLogsResourceOptions, setFlowLogsResourceOptions] = useState<AWSFlowLogsResourceOptionsResponse | null>(null);
  const [flowLogsResourceLoading, setFlowLogsResourceLoading] = useState(false);
  const [selectedFlowLogVPCIDs, setSelectedFlowLogVPCIDs] = useState<string[]>([]);
  const [selectedFlowLogTGWIDs, setSelectedFlowLogTGWIDs] = useState<string[]>([]);
  const [cloudWatchLogsPickerOpen, setCloudWatchLogsPickerOpen] = useState(false);
  const [cloudWatchLogsPickerRow, setCloudWatchLogsPickerRow] = useState<CloudTrailSiteRow | null>(null);
  const [cloudWatchLogsInput, setCloudWatchLogsInput] = useState('');
  const [cloudWatchLogsAPIKey, setCloudWatchLogsAPIKey] = useState<string | null>(null);
  const [azureResourceChangeRows, setAzureResourceChangeRows] = useState<AzureResourceChangesRow[]>([]);
  const [azureResourceChangesLoading, setAzureResourceChangesLoading] = useState(false);
  const [azureResourceChangesBusy, setAzureResourceChangesBusy] = useState<string | null>(null);
  const [azureResourceChangesSecret, setAzureResourceChangesSecret] = useState<{ subscriptionId: string; secret: string; launchUrl: string } | null>(null);
  const [selectedCloudProvider, setSelectedCloudProvider] = useState<CloudProvider | ''>('');
  const [selectedCloudSignal, setSelectedCloudSignal] = useState<CloudSignal | ''>('');
  const bootstrapProviderDefinitions = useMemo(
    () => providerDefinitions.filter((item) => supportsProviderBootstrap(item)),
    [providerDefinitions]
  );
  const setupProviderDefinitions = useMemo(
    () => bootstrapProviderDefinitions.filter((item) => item.bootstrap?.type === 'oauth_start'),
    [bootstrapProviderDefinitions]
  );
  const [deleteDialog, setDeleteDialog] = useState<DeleteDialogState | null>(null);
  const [deleteToast, setDeleteToast] = useState<DeleteToastState | null>(null);

  const selectedCloudSignalOptions = useMemo(
    () => (selectedCloudProvider ? cloudSignalOptions[selectedCloudProvider] : []),
    [selectedCloudProvider]
  );

  const selectedCloudSignalOption = useMemo(
    () => selectedCloudSignalOptions.find((option) => option.value === selectedCloudSignal) ?? null,
    [selectedCloudSignal, selectedCloudSignalOptions]
  );

  const selectedCloudSignalLabel = selectedCloudSignalOption?.label ?? 'CloudTrail';

  const [targetDrawerOpen, setTargetDrawerOpen] = useState(false);
  const [targetDrawerMode, setTargetDrawerMode] = useState<'create' | 'detail'>('create');
  const [targetRecord, setTargetRecord] = useState<InteractionTarget | null>(null);
  const [targetForm, setTargetForm] = useState<TargetFormState>(defaultTargetForm);

  const [ruleDrawerOpen, setRuleDrawerOpen] = useState(false);
  const [ruleDrawerMode, setRuleDrawerMode] = useState<'create' | 'detail'>('create');
  const [ruleRecord, setRuleRecord] = useState<InteractionRoutingRule | null>(null);
  const [ruleForm, setRuleForm] = useState<RuleFormState>(defaultRuleForm);

  const toCloudTrailStatusBadge = useCallback((status: CloudTrailSiteRow['status']) => {
    switch (status) {
      case 'enabled':
        return { label: 'Enabled', className: 'ui-badge ui-badge--success' };
      case 'disabled':
        return { label: 'Disabled', className: 'ui-badge ui-badge--warning' };
      default:
        return { label: 'Action required', className: 'ui-badge' };
    }
  }, []);

  const buildCloudTrailRows = useCallback((sites: Site[], integrations: AWSCloudLogIntegration[]): CloudTrailSiteRow[] => {
    const awsSites = sites as AwsCloudSite[];
    const integrationsBySite = new Map<string, AWSCloudLogIntegration>();
    for (const item of integrations) {
      const siteId = item.siteId?.trim();
      if (siteId) {
        integrationsBySite.set(siteId, item);
      }
    }

    return awsSites
      .filter((site) => site.siteType === 'awsCloud' && Boolean(site.id?.trim()) && Boolean(site.awsAccountId?.trim()) && Boolean(site.awsRegion?.trim()))
      .map((site) => {
        const siteId = site.id!.trim();
        const integration = integrationsBySite.get(siteId) ?? null;
        const snapshot = flowLogsSnapshot(integration);
        const cloudWatchSnapshot = cloudWatchLogGroups(integration);
        const status: CloudTrailSiteRow['status'] =
          integration?.status === 'enabled' || integration?.status === 'disabled' ? integration.status : 'action_required';
        return {
          siteId,
          siteName: site.name?.trim() || 'Unnamed AWS site',
          awsAccountId: site.awsAccountId?.trim() || '',
          region: site.awsRegion?.trim() || '',
          integration,
          status,
          vpcIds: snapshot.vpcIds,
          transitGatewayIds: snapshot.transitGatewayIds,
          logGroups: cloudWatchSnapshot.logGroups,
          resourceSnapshotAt: snapshot.resourceSnapshotAt,
          updatedAt: integration?.updatedAt
        };
      })
      .sort((left, right) => left.siteName.localeCompare(right.siteName));
  }, []);

  const mergeCloudTrailIntegration = useCallback(
    (row: CloudTrailSiteRow, integration: AWSCloudLogIntegration | null): CloudTrailSiteRow => {
      const snapshot = flowLogsSnapshot(integration);
      const cloudWatchSnapshot = cloudWatchLogGroups(integration);
      return {
        ...row,
        integration,
        status: integration?.status === 'enabled' || integration?.status === 'disabled' ? integration.status : 'action_required',
        vpcIds: snapshot.vpcIds,
        transitGatewayIds: snapshot.transitGatewayIds,
        logGroups: cloudWatchSnapshot.logGroups,
        resourceSnapshotAt: snapshot.resourceSnapshotAt,
        updatedAt: integration?.updatedAt
      };
    },
    []
  );

  const updateFilters = useCallback((next: Partial<FilterState>) => {
    setFilters((prev) => ({ ...prev, ...next }));
    setPage(1);
  }, []);

  const resetFilters = useCallback(() => {
    setFilters({ status: 'all', provider: 'all', q: '' });
    setPage(1);
  }, []);

  const createSecret = useCallback(
    async (secretValue: string, label: string) => {
      if (!orgId) {
        throw new Error('Select an organization first.');
      }
      const payload = await secretsApi.orgsOrgIdSecretsPost(
        {
          orgId,
          secretCreate: {
          type: 'password',
          value: secretValue,
          metadata: {
            label
          }
          }
        },
        { cache: 'no-store' }
      );
      if (!payload.id) {
        throw new Error('Secret response missing id.');
      }
      return payload.id;
    },
    [orgId, secretsApi]
  );

  const fetchProviderDefinitions = useCallback(async () => {
    if (!orgId) {
      setProviderDefinitions([]);
      return;
    }
    try {
      const payload: APIProviderDefinitionsListResponse = await integrationsApi.orgsOrgIdIntegrationsProviderDefinitionsGet({ orgId }, { cache: 'no-store' });
      setProviderDefinitions(listResponseItems(payload.items).filter((item) => item.enabled !== false));
    } catch (error) {
      setErrorMessage(await resolveErrorMessage(error, 'Failed to load provider definitions.'));
    }
  }, [integrationsApi, orgId]);

  const connectionRequestId = useRef(0);
  const fetchConnections = useCallback(async () => {
    const requestId = ++connectionRequestId.current;
    if (!orgId) {
      setConnections([]);
      setConnectionsTotal(0);
      setLoadingConnections(false);
      return;
    }
    setLoadingConnections(true);
    try {
      const payload: APIIntegrationConnectionsListResponse = await integrationsApi.listIntegrationConnections({ orgId, limit: pageSize, offset: (page - 1) * pageSize, q: filters.q.trim() || undefined, provider: filters.provider !== 'all' ? filters.provider : undefined, status: filters.status !== 'all' ? filters.status : undefined }, { cache: 'no-store' });
      if (requestId !== connectionRequestId.current) return;
      setConnections(listResponseItems(payload.items).map(mapIntegrationConnectionRecord));
      setConnectionsTotal(payload.meta.total ?? 0);
    } catch (error) {
      const message = await resolveErrorMessage(error, 'Failed to load connections.');
      if (requestId === connectionRequestId.current) setErrorMessage(message);
    } finally {
      if (requestId === connectionRequestId.current) setLoadingConnections(false);
    }
  }, [integrationsApi, orgId, page, pageSize, filters]);

  useEffect(() => {
    if (bootstrapProviderDefinitions.length === 0) {
      return;
    }
    const params = new URLSearchParams(window.location.search);
    const definition = bootstrapProviderDefinitions.find((item) => {
      const statusQueryParam = item.bootstrap?.statusQueryParam?.trim();
      return Boolean(statusQueryParam && params.has(statusQueryParam));
    });
    const bootstrap = definition?.bootstrap;
    const statusQueryParam = bootstrap?.statusQueryParam?.trim();
    if (!definition || !bootstrap || !statusQueryParam) {
      return;
    }
    const status = params.get(statusQueryParam)?.trim();
    if (status === bootstrap.successStatusValue) {
      setIntegrationStatusMessage(`${definition.displayName} connection created successfully.`);
      setErrorMessage(null);
      void fetchConnections();
    } else if (status === 'cancelled') {
      setIntegrationStatusMessage(`${definition.displayName} setup was cancelled.`);
      setErrorMessage(null);
    } else if (status === bootstrap.errorStatusValue) {
      setIntegrationStatusMessage(null);
      setErrorMessage(`${definition.displayName} connection could not be completed. Please try Connect ${definition.displayName} again.`);
    }
    params.delete(statusQueryParam);
    const nextQuery = params.toString();
    const nextURL = nextQuery ? `${window.location.pathname}?${nextQuery}${window.location.hash}` : `${window.location.pathname}${window.location.hash}`;
    window.history.replaceState({}, document.title, nextURL);
  }, [bootstrapProviderDefinitions, fetchConnections]);

  const fetchTargets = useCallback(async () => {
    if (!orgId) {
      setTargets([]);
      return;
    }
    setLoadingTargets(true);
    try {
      const payload: APIInteractionTargetsListResponse = await integrationsApi.listInteractionTargets({ orgId }, { cache: 'no-store' });
      setTargets(listResponseItems(payload.items).map(mapInteractionTargetRecord));
    } catch (error) {
      setErrorMessage(await resolveErrorMessage(error, 'Failed to load interaction targets.'));
    } finally {
      setLoadingTargets(false);
    }
  }, [integrationsApi, orgId]);

  const fetchRoutingRules = useCallback(async () => {
    if (!orgId) {
      setRoutingRules([]);
      return;
    }
    setLoadingRules(true);
    try {
      const payload: APIInteractionRoutingRulesListResponse = await integrationsApi.listInteractionRoutingRules({ orgId }, { cache: 'no-store' });
      setRoutingRules(listResponseItems(payload.items).map(mapInteractionRoutingRuleRecord));
    } catch (error) {
      setErrorMessage(await resolveErrorMessage(error, 'Failed to load interaction routing rules.'));
    } finally {
      setLoadingRules(false);
    }
  }, [integrationsApi, orgId]);

  useEffect(() => { void fetchProviderDefinitions(); }, [fetchProviderDefinitions]);
  useEffect(() => {
    void fetchConnections();
    return () => { connectionRequestId.current += 1; };
  }, [fetchConnections]);
  useEffect(() => { void fetchTargets(); }, [fetchTargets]);
  useEffect(() => { void fetchRoutingRules(); }, [fetchRoutingRules]);

  const fetchCloudTrailIntegrations = useCallback(async () => {
    if (!orgId) {
      setCloudTrailItems([]);
      return;
    }
    setCloudTrailLoading(true);
    setCloudTrailErrorMessage(null);
    try {
      const sitesPromise = sitesApi.orgsOrgIdSitesGet({ orgId, limit: 500 }, { cache: 'no-store' });
      const integrationPromise =
        selectedCloudSignal === 'flowlogs'
          ? cloudIntegrationsApi.orgsOrgIdIntegrationsAwsFlowlogsGet({ orgId, limit: 500, offset: 0 }, { cache: 'no-store' })
          : selectedCloudSignal === 'cloudwatch-logs'
            ? cloudIntegrationsApi.orgsOrgIdIntegrationsAwsCloudwatchLogsGet({ orgId, limit: 500, offset: 0 }, { cache: 'no-store' })
            : cloudIntegrationsApi.orgsOrgIdIntegrationsAwsCloudtrailGet({ orgId, limit: 500, offset: 0 }, { cache: 'no-store' });
      const [sitesPayload, integrationsPayload] = await Promise.all([sitesPromise, integrationPromise]);
      setCloudTrailItems(buildCloudTrailRows(sitesPayload.items, integrationsPayload.items ?? []));
    } catch (error) {
      setCloudTrailErrorMessage(await resolveErrorMessage(error, `Failed to load ${selectedCloudSignalLabel} integrations.`));
    } finally {
      setCloudTrailLoading(false);
    }
  }, [buildCloudTrailRows, cloudIntegrationsApi, orgId, selectedCloudSignal, selectedCloudSignalLabel, sitesApi]);

  const fetchAzureResourceChanges = useCallback(async () => {
    if (!orgId) {
      setAzureResourceChangeRows([]);
      return;
    }
    setAzureResourceChangesLoading(true);
    setCloudTrailErrorMessage(null);
    try {
      const headers = await cloneHeadersWithAuth({ Accept: 'application/json' });
      const [sites, items] = await Promise.all([
        collectCursorPages((after) => sitesApi.orgsOrgIdSitesGet({ orgId, limit: 200, after }, { cache: 'no-store' })),
        collectOffsetPages(async (offset) => {
          const response = await fetchWithAuthRetry(buildOrgUrl(basePath, orgId, `/integrations/azure/resource-changes?limit=200&offset=${offset}`), { headers, cache: 'no-store' });
          if (!response.ok) throw new ApiResponseError(response, 'Failed to load Azure resource-change integrations.');
          return (await response.json()) as { items?: AzureResourceChangesIntegration[]; meta?: { exactTotal?: number } };
        }, 200)
      ]);
      const integrations = new Map(items.flatMap((item) => item.subscriptionId ? [[item.subscriptionId, item] as const] : []));
      const rows = new Map<string, AzureResourceChangesRow>();
      for (const site of sites as AzureCloudSite[]) {
        const subscriptionId = site.azureSubscriptionId?.trim() || site.subscriptionId?.trim();
        if (!subscriptionId || rows.has(subscriptionId)) continue;
        rows.set(subscriptionId, { siteId: site.id?.trim() ?? '', siteName: site.name?.trim() || 'Azure subscription', subscriptionId, integration: integrations.get(subscriptionId) ?? null });
      }
      for (const [subscriptionId, integration] of integrations) {
        if (!rows.has(subscriptionId)) rows.set(subscriptionId, { siteId: '', siteName: integration.name?.trim() || 'Azure subscription', subscriptionId, integration });
      }
      setAzureResourceChangeRows([...rows.values()].sort((left, right) => left.subscriptionId.localeCompare(right.subscriptionId)));
    } catch (error) {
      setCloudTrailErrorMessage(await resolveErrorMessage(error, 'Failed to load Azure resource-change integrations.'));
    } finally {
      setAzureResourceChangesLoading(false);
    }
  }, [basePath, fetchWithAuthRetry, orgId, sitesApi]);

  useEffect(() => {
    if (selectedCloudProvider === 'azure' && selectedCloudSignal === 'resource-changes') {
      void fetchAzureResourceChanges();
      return;
    }
    if (selectedCloudProvider === 'aws' && (selectedCloudSignal === 'cloudtrail' || selectedCloudSignal === 'flowlogs' || selectedCloudSignal === 'cloudwatch-logs')) {
      void fetchCloudTrailIntegrations();
      return;
    }
    setCloudTrailItems([]);
    setCloudTrailErrorMessage(null);
  }, [fetchAzureResourceChanges, fetchCloudTrailIntegrations, selectedCloudProvider, selectedCloudSignal]);

  const fetchWebhookProvisioning = useCallback(
    async (connection: IntegrationConnection) => {
      if (!orgId || !connection.id) {
        return;
      }
      setWebhookLoading(true);
      setWebhookError(null);
      try {
        const payload = await integrationsApi.getIntegrationConnectionWebhookProvisioning(
          { orgId, connectionId: connection.id },
          { cache: 'no-store' }
        );
        setWebhookProvisioning(mapIntegrationWebhookProvisioning(payload));
      } catch (error) {
        setWebhookError(await resolveErrorMessage(error, 'Failed to load webhook details.'));
        throw error;
      } finally {
        setWebhookLoading(false);
      }
    },
    [integrationsApi, orgId]
  );

  const copyToClipboard = useCallback(async (label: string, value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopyFeedback(`${label} copied.`);
      window.setTimeout(() => setCopyFeedback(null), 1800);
    } catch {
      setCopyFeedback(`Failed to copy ${label.toLowerCase()}.`);
      window.setTimeout(() => setCopyFeedback(null), 1800);
    }
  }, []);

  const updateConnectionProvider = useCallback((provider: string) => {
    setConnectionForm((prev) => ({
      provider,
      name: prev.provider === provider && prev.name ? prev.name : providerDefinitions.find((item) => item.providerKey === provider)?.displayName ?? provider,
      values: {}
    }));
  }, [providerDefinitions]);

  const updateConnectionName = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setConnectionForm((prev) => ({ ...prev, name: value }));
  }, []);

  const updateConnectionValue =
    (fieldKey: string) => (event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const element = event.target as HTMLInputElement;
      const value = element.type === 'checkbox' ? (element.checked ? 'true' : 'false') : element.value;
      setConnectionForm((prev) => ({
        ...prev,
        values: {
          ...prev.values,
          [fieldKey]: value
        }
      }));
    };

  const updateCMDBReconciliationField =
    (field: keyof CMDBReconciliationFormState) =>
    (event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const element = event.target as HTMLInputElement;
      const value = element.type === 'checkbox' ? element.checked : element.value;
      setCMDBReconciliationForm((prev) => ({ ...prev, [field]: value } as CMDBReconciliationFormState));
    };

  const updateTargetFormField =
    (field: keyof TargetFormState) => (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
      const element = event.target as HTMLInputElement;
      const value = element.type === 'checkbox' ? element.checked : element.value;
      setTargetForm((prev) => {
        if (field === 'provider') {
          const provider = String(value).trim();
          return {
            ...prev,
            provider,
            connectionId: interactionConnections.find((item) => item.provider === provider)?.id ?? ''
          };
        }
        return { ...prev, [field]: value } as TargetFormState;
      });
    };

  const updateRuleFormField =
    (field: keyof RuleFormState) => (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
      const element = event.target as HTMLInputElement;
      const value = element.type === 'checkbox' ? element.checked : element.value;
      setRuleForm((prev) => {
        if (field === 'provider') {
          const provider = String(value).trim();
          return {
            ...prev,
            provider,
            connectionId: interactionConnections.find((item) => item.provider === provider)?.id ?? ''
          };
        }
        return { ...prev, [field]: value } as RuleFormState;
      });
    };

  const buildConnectionPayload = useCallback(
    async (formState: ConnectionFormState, existing?: IntegrationConnection | null) => {
      const definition = providerDefinitions.find((item) => item.providerKey === formState.provider);
      return buildIntegrationConnectionPayload({
        provider: formState.provider,
        values: formState.values,
        fields: definition?.fields ?? [],
        existingConfig: existing?.config,
        createSecret
      });
    },
    [createSecret, providerDefinitions]
  );

  const resolveProviderDefinition = useCallback(
    (providerKey?: string | null) => providerDefinitions.find((item) => item.providerKey === (providerKey ?? '')),
    [providerDefinitions]
  );

  const persistConnection = useCallback(async (): Promise<IntegrationConnection> => {
      if (!orgId) {
        throw new Error('Select an organization first.');
      }
      const config = await buildConnectionPayload(connectionForm, connectionDrawerMode === 'detail' ? connectionRecord : null);
      const payload = {
        provider: connectionForm.provider,
        name: connectionForm.name.trim() || providerDefinitions.find((item) => item.providerKey === connectionForm.provider)?.displayName || connectionForm.provider,
        config
      };
      const nextRecord = mapIntegrationConnectionRecord(
        connectionDrawerMode === 'create'
          ? await integrationsApi.createIntegrationConnection({ orgId, integrationConnectionCreate: payload }, { cache: 'no-store' })
          : await integrationsApi.updateIntegrationConnection(
            {
              orgId,
              connectionId: connectionRecord?.id ?? '',
              integrationConnectionUpdate: payload
            },
            { cache: 'no-store' }
          )
      );
      setConnectionRecord(nextRecord);
      setConnectionDrawerMode('detail');
      setConnectionForm(connectionFormFromRecord(nextRecord, providerDefinitions.find((item) => item.providerKey === nextRecord.provider)));
      setHighlightRowId(nextRecord.id ?? null);
      await fetchConnections();
      return nextRecord;
  }, [buildConnectionPayload, connectionDrawerMode, connectionForm, connectionRecord, fetchConnections, integrationsApi, orgId, providerDefinitions]);

  const submitConnection = useCallback(async () => {
    setSavingConnection(true);
    setErrorMessage(null);
    try {
      await persistConnection();
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Failed to save integration connection.');
    } finally {
      setSavingConnection(false);
    }
  }, [persistConnection]);

  const deleteConnection = useCallback(
    async (connection: IntegrationConnection, options?: { successMessage?: string }) => {
      if (!orgId || !connection.id) {
        return;
      }
      setRowActionBusy(connection.id);
      setErrorMessage(null);
      try {
        await integrationsApi.deleteIntegrationConnection({ orgId, connectionId: connection.id }, { cache: 'no-store' });
        if (connectionRecord?.id === connection.id) {
          setConnectionDrawerOpen(false);
          setConnectionRecord(null);
        }
        setHighlightRowId((current) => (current === connection.id ? null : current));
        setConnections((current) => current.filter((item) => item.id !== connection.id));
        setDeleteDialog(null);
        setDeleteToast({
          tone: 'success',
          message: options?.successMessage ?? 'Integration deleted successfully.'
        });
        await fetchConnections();
      } catch (error) {
        const message = await resolveErrorMessage(error, 'Failed to delete connection.');
        setErrorMessage(message);
        setDeleteToast({ tone: 'error', message });
      } finally {
        setRowActionBusy(null);
      }
    },
    [connectionRecord?.id, fetchConnections, integrationsApi, orgId]
  );

  const testConnection = useCallback(
    async (connection: IntegrationConnection) => {
      if (!orgId || !connection.id) {
        return;
      }
      setRowActionBusy(connection.id);
      setErrorMessage(null);
      const showInlineResult = connectionDrawerOpen && connectionRecord?.id === connection.id;
      try {
        const result = await integrationsApi.testIntegrationConnection({ orgId, connectionId: connection.id }, { cache: 'no-store' }) as {
          success?: boolean;
          message?: string;
          errorMessage?: string;
        };
        if (result.success === false) {
          throw new Error(result.errorMessage?.trim() || result.message?.trim() || 'Connection test failed.');
        }
        if (showInlineResult) {
          setConnectionTestFeedback({ tone: 'success', message: result.message?.trim() || 'Connection test passed.' });
          window.setTimeout(() => setConnectionTestFeedback(null), CMDB_ACCESS_FEEDBACK_TIMEOUT_MS);
        }
        await fetchConnections();
        if (supportsWebhookSetup(resolveProviderDefinition(connection.provider))) {
          setShowWebhookSetup(true);
          setRevealWebhookSecret(false);
          void fetchWebhookProvisioning(connection).catch(() => undefined);
        }
      } catch (error) {
        const message = await resolveErrorMessage(error, 'Failed to test integration connection.');
        if (showInlineResult) {
          setConnectionTestFeedback({ tone: 'error', message });
          window.setTimeout(() => setConnectionTestFeedback(null), CMDB_ACCESS_FEEDBACK_TIMEOUT_MS);
        } else {
          setErrorMessage(message);
        }
      } finally {
        setRowActionBusy(null);
      }
    },
    [connectionDrawerOpen, connectionRecord?.id, fetchConnections, fetchWebhookProvisioning, integrationsApi, orgId, resolveProviderDefinition]
  );

  const fetchCMDBWritebackPolicy = useCallback(
    async (connection: IntegrationConnection) => {
      if (!orgId || !connection.id) {
        setCMDBWritebackPolicy(defaultCMDBWritebackPolicy());
        setCMDBWritebackForm(defaultCMDBWritebackForm());
        return;
      }
      setLoadingCMDBWritebackPolicy(true);
      setErrorMessage(null);
      try {
        const payload = await integrationsApi.getIntegrationConnectionCMDBWritebackPolicy(
          {
            orgId,
            connectionId: connection.id
          },
          { cache: 'no-store' }
        );
        const nextPolicy = mapCMDBWritebackPolicy(payload);
        setCMDBWritebackPolicy(nextPolicy);
        setCMDBWritebackForm({
          updateExisting: nextPolicy.updateExisting,
          createMissing: nextPolicy.createMissing,
          mode: nextPolicy.mode,
          publicationEnabled: nextPolicy.publicationEnabled,
          allowedFields: nextPolicy.allowedFields
        });
      } catch (error) {
        setErrorMessage(error instanceof Error ? error.message : 'Failed to load CMDB writeback policy.');
        setCMDBWritebackPolicy(defaultCMDBWritebackPolicy());
        setCMDBWritebackForm(defaultCMDBWritebackForm());
      } finally {
        setLoadingCMDBWritebackPolicy(false);
      }
    },
    [integrationsApi, orgId]
  );

  const fetchCMDBPolicyAudit = useCallback(
    async (connectionId?: string) => {
      if (!orgId || !connectionId) {
        setCMDBPolicyAuditEvents([]);
        setCMDBPolicyAuditError(null);
        return;
      }
      setLoadingCMDBPolicyAudit(true);
      setCMDBPolicyAuditError(null);
      try {
        const response = await auditLogApi.orgsOrgIdAuditEventsGet(
          {
            orgId,
            limit: 25,
            type: CMDB_WRITEBACK_POLICY_AUDIT_EVENT,
            resourceType: 'integration_connection',
            resourceId: connectionId
          },
          { cache: 'no-store' }
        );
        setCMDBPolicyAuditEvents(response.items);
      } catch (error) {
        setCMDBPolicyAuditEvents([]);
        setCMDBPolicyAuditError(await resolveErrorMessage(error, 'Failed to load policy audit history.'));
      } finally {
        setLoadingCMDBPolicyAudit(false);
      }
    },
    [auditLogApi, orgId]
  );

  const fetchCMDBReconciliationAudit = useCallback(async () => {
    if (!orgId) {
      setCMDBReconciliationAuditEvents([]);
      setCMDBReconciliationAuditError(null);
      return;
    }
    setLoadingCMDBReconciliationAudit(true);
    setCMDBReconciliationAuditError(null);
    try {
      const response = await auditLogApi.orgsOrgIdAuditEventsGet(
        {
          orgId,
          limit: 50,
          type: CMDB_WRITEBACK_POLICY_AUDIT_EVENT,
          resourceType: 'organization',
          resourceId: orgId
        },
        { cache: 'no-store' }
      );
      setCMDBReconciliationAuditEvents(response.items.filter((event) => event.metadata?.policyType === 'cmdb_reconciliation'));
    } catch (error) {
      setCMDBReconciliationAuditEvents([]);
      setCMDBReconciliationAuditError(await resolveErrorMessage(error, 'Failed to load reconciliation audit history.'));
    } finally {
      setLoadingCMDBReconciliationAudit(false);
    }
  }, [auditLogApi, orgId]);

  const fetchCMDBReconciliationPolicy = useCallback(async (connectionId?: string) => {
    if (!orgId) {
      setCMDBReconciliationPolicy(defaultCMDBReconciliationPolicy());
      setCMDBReconciliationForm(defaultCMDBReconciliationForm());
      setCMDBMaturityScore(null);
      return;
    }
    setLoadingCMDBReconciliationPolicy(true);
    setErrorMessage(null);
    try {
      const [policyPayload, maturityPayload] = await Promise.all([
        reconciliationApi.getCMDBReconciliationPolicy({ orgId }, { cache: 'no-store' }),
        reconciliationApi.getCMDBMaturity({ orgId, connectionId }, { cache: 'no-store' }).catch(() => null)
      ]);
      const nextPolicy = mapCMDBReconciliationPolicy(policyPayload);
      setCMDBReconciliationPolicy(nextPolicy);
      setCMDBReconciliationForm(policyToCMDBReconciliationForm(nextPolicy));
      setCMDBMaturityScore(mapCMDBMaturityScore(maturityPayload));
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Failed to load CMDB reconciliation policy.');
      setCMDBReconciliationPolicy(defaultCMDBReconciliationPolicy());
      setCMDBReconciliationForm(defaultCMDBReconciliationForm());
      setCMDBMaturityScore(null);
    } finally {
      setLoadingCMDBReconciliationPolicy(false);
    }
  }, [reconciliationApi, orgId]);

  const fetchCMDBSourceEvidence = useCallback(async (connectionId: string) => {
    if (!orgId) {
      setCMDBSourceEvidence(null);
      setCMDBSourceRecords([]);
      setCMDBSourceRetention(null);
      return;
    }
    setLoadingCMDBSourceEvidence(true);
    setCMDBSourceRecordHistory(null);
    setCMDBSourceIdentityHistory(null);
    try {
      const [summary, records, retention] = await Promise.all([
        integrationsApi.getIntegrationConnectionCMDBSourceEvidence({ orgId, connectionId }, { cache: 'no-store' }),
        integrationsApi.listIntegrationConnectionCMDBSourceRecords({ orgId, connectionId, limit: 20, offset: 0 }, { cache: 'no-store' }),
        integrationsApi.getIntegrationConnectionCMDBSourceRetentionPolicy({ orgId, connectionId }, { cache: 'no-store' })
      ]);
      setCMDBSourceEvidence(summary);
      setCMDBSourceRecords(records.items);
      setCMDBSourceRetention(retention);
    } catch (error) {
      setCMDBSourceEvidence(null);
      setCMDBSourceRecords([]);
      setCMDBSourceRetention(null);
      setErrorMessage(error instanceof Error ? error.message : 'Failed to load CMDB source evidence.');
    } finally {
      setLoadingCMDBSourceEvidence(false);
    }
  }, [integrationsApi, orgId]);

  const fetchCMDBDeviceCrosswalk = useCallback(async (connectionId: string) => {
    if (!orgId) return;
    setLoadingCMDBDeviceCrosswalk(true);
    try {
      const crosswalk = await reconciliationApi.getCMDBDeviceCrosswalk({ orgId, connectionId }, { cache: 'no-store' });
      setCMDBDeviceCrosswalk(crosswalk);
    } catch (error) {
      setErrorMessage(await resolveErrorMessage(error, 'Failed to load CMDB device matches.'));
    } finally {
      setLoadingCMDBDeviceCrosswalk(false);
    }
  }, [reconciliationApi, orgId]);

  const startCMDBSync = useCallback(async (connectionId: string) => {
    if (!orgId) return;
    setErrorMessage(null);
    setCMDBSyncBusy(true);
    try {
      const job = await integrationsApi.enqueueIntegrationConnectionCMDBSync({
        orgId,
        connectionId,
        integrationConnectionCMDBSyncRequest: { requestId: crypto.randomUUID() }
      });
      setCMDBSyncJob(job);
      if (job.status === 'succeeded') {
        setCMDBSyncBusy(false);
        await Promise.all([
          fetchCMDBSourceEvidence(connectionId),
          fetchCMDBDeviceCrosswalk(connectionId)
        ]);
      } else if (job.status === 'failed') {
        setCMDBSyncBusy(false);
        setErrorMessage(job.errorSummary || 'CMDB synchronization failed.');
      }
    } catch (error) {
      setErrorMessage(await resolveErrorMessage(error, 'Failed to start CMDB synchronization.'));
      setCMDBSyncBusy(false);
    }
  }, [fetchCMDBDeviceCrosswalk, fetchCMDBSourceEvidence, integrationsApi, orgId]);

  const reviewCMDBIdentity = useCallback(async (input: { findingId: string; expectedVersion: number; action: string; reapEntityId?: string; assignedTo?: string; reason: string }) => {
    if (!orgId || !connectionRecord?.id) return;
    try {
      await integrationsApi.adjudicateIntegrationConnectionCMDBIdentityFinding({
        orgId,
        connectionId: connectionRecord.id,
        findingId: input.findingId,
        cMDBIdentityAdjudicationRequest: {
          action: input.action as 'confirm_candidate' | 'replace_mapping' | 'reject' | 'defer' | 'quarantine' | 'assign' | 'reopen',
          expectedVersion: input.expectedVersion,
          reason: input.reason,
          reapEntityId: input.reapEntityId,
          assignedTo: input.assignedTo
        }
      });
      setCopyFeedback('Identity decision saved. Future synchronization will retain this reviewed mapping.');
      await fetchCMDBDeviceCrosswalk(connectionRecord.id);
    } catch (error) {
      setErrorMessage(await resolveErrorMessage(error, 'Failed to save the identity decision.'));
      throw error;
    }
  }, [connectionRecord?.id, fetchCMDBDeviceCrosswalk, integrationsApi, orgId]);

  const createMissingCMDBCI = useCallback(async ({ deviceId, operationId }: { deviceId: string; operationId: string }) => {
    if (!orgId || !connectionRecord?.id) {
      throw new Error('Select an active CMDB connection before creating a CI.');
    }
    try {
      const response = await integrationsApi.writebackIntegrationConnectionCMDB({
        orgId,
        connectionId: connectionRecord.id,
        integrationConnectionCMDBPublicationRequest: { deviceIds: [deviceId], operationId }
      });
      const result = response.items.find((item) => item.deviceId === deviceId);
      if (!result) {
        throw new Error('The CMDB writeback did not return a result for this device.');
      }
      await Promise.all([
        fetchCMDBDeviceCrosswalk(connectionRecord.id),
        fetchCMDBSourceEvidence(connectionRecord.id)
      ]);
      return result;
    } catch (error) {
      setErrorMessage(await resolveErrorMessage(error, 'Failed to create the ServiceNow CI.'));
      throw error;
    }
  }, [connectionRecord?.id, fetchCMDBDeviceCrosswalk, fetchCMDBSourceEvidence, integrationsApi, orgId]);

  const fetchCMDBSourceRecordHistory = useCallback(async (connectionId: string, sourceIdentityId: string) => {
    if (!orgId) return;
    setLoadingCMDBSourceEvidence(true);
    try {
      const [recordHistory, identityHistory] = await Promise.all([
        integrationsApi.getIntegrationConnectionCMDBSourceRecordHistory({ orgId, connectionId, sourceIdentityId }, { cache: 'no-store' }),
        integrationsApi.getIntegrationConnectionCMDBIdentityHistory({ orgId, connectionId, sourceIdentityId }, { cache: 'no-store' })
      ]);
      setCMDBSourceRecordHistory(recordHistory);
      setCMDBSourceIdentityHistory(identityHistory);
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Failed to load source identity history.');
    } finally { setLoadingCMDBSourceEvidence(false); }
  }, [integrationsApi, orgId]);

  const saveCMDBSourceRetention = useCallback(async (connectionId: string) => {
    if (!orgId || !cmdbSourceRetention) return;
    setLoadingCMDBSourceEvidence(true);
    try {
      const policy = await integrationsApi.updateIntegrationConnectionCMDBSourceRetentionPolicy({ orgId, connectionId, cMDBSourceRetentionPolicyUpdateRequest: { expectedVersion: cmdbSourceRetention.version, sourceVersionDays: cmdbSourceRetention.sourceVersionDays, occurrenceDays: cmdbSourceRetention.occurrenceDays, identityDecisionDays: cmdbSourceRetention.identityDecisionDays, findingDays: cmdbSourceRetention.findingDays } });
      setCMDBSourceRetention(policy);
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Failed to save source retention policy.');
    } finally {
      setLoadingCMDBSourceEvidence(false);
    }
  }, [cmdbSourceRetention, integrationsApi, orgId]);

  const saveCMDBReconciliationPolicy = useCallback(async () => {
    if (!orgId) {
      throw new Error('Select an organization first.');
    }
    setLoadingCMDBReconciliationPolicy(true);
    try {
      const manualScore = cmdbReconciliationForm.manualMaturityScore.trim();
      const parsedManualScore = manualScore === '' ? undefined : Number(manualScore);
      const payload = await reconciliationApi.updateCMDBReconciliationPolicy(
        {
          orgId,
          cMDBReconciliationPolicyUpdateRequest: {
            explicitMappingRequired: cmdbReconciliationForm.explicitMappingRequired,
            manualMaturityLevel: cmdbReconciliationForm.manualMaturityLevel || undefined,
            manualMaturityScore: parsedManualScore
          }
        },
        { cache: 'no-store' }
      );
      const nextPolicy = mapCMDBReconciliationPolicy(payload);
      setCMDBReconciliationPolicy(nextPolicy);
      setCMDBReconciliationForm(policyToCMDBReconciliationForm(nextPolicy));
      const maturityPayload = await reconciliationApi
        .getCMDBMaturity({ orgId, connectionId: connectionRecord?.id }, { cache: 'no-store' })
        .catch(() => null);
      setCMDBMaturityScore(mapCMDBMaturityScore(maturityPayload));
      window.setTimeout(() => void fetchCMDBReconciliationAudit(), 1500);
      return nextPolicy;
    } finally {
      setLoadingCMDBReconciliationPolicy(false);
    }
  }, [cmdbReconciliationForm, connectionRecord?.id, reconciliationApi, fetchCMDBReconciliationAudit, orgId]);

  const saveCMDBWritebackPolicy = useCallback(
    async (connection: IntegrationConnection) => {
      if (!orgId || !connection.id) {
        throw new Error('Select an organization first.');
      }
      setLoadingCMDBWritebackPolicy(true);
      try {
        const payload = await integrationsApi.updateIntegrationConnectionCMDBWritebackPolicy(
          {
            orgId,
            connectionId: connection.id,
            integrationConnectionCMDBWritebackPolicyUpdateRequest: {
              updateExisting: cmdbWritebackForm.updateExisting,
              createMissing: cmdbWritebackForm.createMissing,
              mode: cmdbWritebackForm.mode,
              publicationEnabled: cmdbWritebackForm.publicationEnabled,
              allowedFields: (cmdbWritebackForm.updateExisting ? cmdbWritebackForm.allowedFields : []) as IntegrationConnectionCMDBWritebackPolicyUpdateRequest['allowedFields']
            }
          },
          { cache: 'no-store' }
        );
        const nextPolicy = mapCMDBWritebackPolicy(payload);
        setCMDBWritebackPolicy(nextPolicy);
        setCMDBWritebackForm({
          updateExisting: nextPolicy.updateExisting,
          createMissing: nextPolicy.createMissing,
          mode: nextPolicy.mode,
          publicationEnabled: nextPolicy.publicationEnabled,
          allowedFields: nextPolicy.allowedFields
        });
        setCMDBAccessCheckResult(null);
        window.setTimeout(() => void fetchCMDBPolicyAudit(connection.id), 1500);
        return nextPolicy;
      } finally {
        setLoadingCMDBWritebackPolicy(false);
      }
    },
    [cmdbWritebackForm, fetchCMDBPolicyAudit, integrationsApi, orgId]
  );

  const checkCMDBAccess = useCallback(
    async (connection: IntegrationConnection, forceWriteValidation = false) => {
      if (!orgId || !connection.id) {
        return;
      }
      setCMDBAccessCheckBusy(true);
      setErrorMessage(null);
      try {
        const request: IntegrationConnectionCMDBAccessCheckRequest = forceWriteValidation ? { forceWriteValidation: true } : {};
        const payload = await integrationsApi.checkIntegrationConnectionCMDBAccess(
          {
            orgId,
            connectionId: connection.id,
            integrationConnectionCMDBAccessCheckRequest: request
          },
          { cache: 'no-store' }
        );
        setCMDBAccessCheckResult(payload);
        setCopyFeedback(
          `CMDB access check completed. Read ${payload.readPassedCount}/${payload.readRequiredCount} passed. Write ${payload.writePassedCount}/${payload.writeRequiredCount} passed.`
        );
        window.setTimeout(() => setCopyFeedback(null), CMDB_ACCESS_FEEDBACK_TIMEOUT_MS);
        await fetchConnections();
      } catch (error) {
        setErrorMessage(error instanceof Error ? error.message : 'Failed to check CMDB access.');
      } finally {
        setCMDBAccessCheckBusy(false);
      }
    },
    [fetchConnections, integrationsApi, orgId]
  );

  const openCreateConnectionDrawer = useCallback(() => {
    const firstProvider = providerDefinitions[0];
    setConnectionDrawerMode('create');
    setConnectionDrawerTab('overview');
    setCMDBWorkspaceTab('access');
    setConnectionTestFeedback(null);
    setConnectionRecord(null);
    setConnectionForm(defaultConnectionForm(firstProvider?.providerKey ?? '', firstProvider?.displayName ?? ''));
    setCMDBWritebackPolicy(defaultCMDBWritebackPolicy());
    setCMDBWritebackForm(defaultCMDBWritebackForm());
    setCMDBReconciliationPolicy(defaultCMDBReconciliationPolicy());
    setCMDBReconciliationForm(defaultCMDBReconciliationForm());
    setCMDBMaturityScore(null);
    setCMDBSourceEvidence(null);
    setCMDBSyncJob(null);
    setCMDBSyncBusy(false);
    setCMDBSourceRecords([]);
    setCMDBSourceRetention(null);
    setCMDBAccessCheckResult(null);
    setWebhookProvisioning(null);
    setWebhookError(null);
    setShowWebhookSetup(false);
    setRevealWebhookSecret(false);
    setConnectionDrawerOpen(true);
  }, [providerDefinitions]);

  const startProviderBootstrap = useCallback(async (definition: ProviderDefinition) => {
    const bootstrap = definition.bootstrap;
    if (!orgId || !hasProviderBootstrapAction(bootstrap)) {
      return;
    }
    const bootstrapAction = bootstrap!;
    setStartingBootstrapProviderKey(definition.providerKey);
    setIntegrationStatusMessage(null);
    setErrorMessage(null);
    try {
      const endpoint = buildOrgUrl(basePath, orgId, bootstrapAction.path);
      const headers = await cloneHeadersWithAuth({ Accept: 'application/json' });
      const response = await fetchWithAuthRetry(
        endpoint,
        buildProviderBootstrapRequest(bootstrapAction, definition.providerKey, headers)
      );
      if (!response.ok) {
        throw new ApiResponseError(response, `Failed to start ${definition.displayName} connection.`);
      }
      if (response.redirected && response.url) {
        window.location.assign(response.url);
        return;
      }
      const contentType = response.headers.get('content-type') ?? '';
      if (contentType.includes('application/json')) {
        const payload = (await response.json()) as { authorizationUrl?: string; redirectUrl?: string };
        const nextURL = payload.authorizationUrl?.trim() || payload.redirectUrl?.trim();
        if (nextURL) {
          window.location.assign(nextURL);
          return;
        }
      }
      window.location.assign(endpoint);
    } catch (error) {
      setErrorMessage(await resolveErrorMessage(error, `Failed to start ${definition.displayName} connection.`));
      setStartingBootstrapProviderKey(null);
    }
  }, [basePath, fetchWithAuthRetry, orgId]);

  const refreshIntegrationSetups = useCallback(async () => {
    if (!orgId || setupProviderDefinitions.length === 0) {
      setIntegrationSetups({});
      return;
    }

    const headers = await cloneHeadersWithAuth({ Accept: 'application/json' });
    const results = await Promise.all(
      setupProviderDefinitions.map(async (definition) => {
        const provider = normalizeProvider(definition.providerKey);
        if (!provider) {
          return null;
        }
        try {
          const response = await fetchWithAuthRetry(
            buildOrgUrl(basePath, orgId, `/integrations/setup/${encodeURIComponent(provider)}`),
            { headers }
          );
          if (!response.ok) {
            return null;
          }
          const setup = await response.json() as IntegrationSetupRequest;
          return setup.id ? { ...setup, provider } : null;
        } catch {
          // Setup status is supplemental UI. The connection list remains usable if it cannot be loaded.
          return null;
        }
      })
    );

    const next: Record<string, IntegrationSetupRequest> = {};
    for (const setup of results) {
      if (setup) {
        next[setup.provider] = setup;
      }
    }
    setIntegrationSetups(next);
  }, [basePath, fetchWithAuthRetry, orgId, setupProviderDefinitions]);

  useEffect(() => { void refreshIntegrationSetups(); }, [refreshIntegrationSetups]);

  const cancelIntegrationSetup = useCallback(async (setup: IntegrationSetupRequest) => {
    if (!orgId || !setup.provider || cancellingSetupProviderKey) {
      return;
    }
    setCancellingSetupProviderKey(setup.provider);
    setErrorMessage(null);
    try {
      const headers = await cloneHeadersWithAuth({ Accept: 'application/json' });
      const response = await fetchWithAuthRetry(
        buildOrgUrl(basePath, orgId, `/integrations/setup/${encodeURIComponent(setup.provider)}`),
        { method: 'DELETE', headers }
      );
      if (!response.ok) {
        throw new ApiResponseError(response, `Unable to cancel ${setup.provider} setup.`);
      }
      setIntegrationStatusMessage(`${setup.provider} setup request cancelled.`);
      await refreshIntegrationSetups();
    } catch (error) {
      setErrorMessage(await resolveErrorMessage(error, `Unable to cancel ${setup.provider} setup.`));
    } finally {
      setCancellingSetupProviderKey(null);
    }
  }, [basePath, cancellingSetupProviderKey, fetchWithAuthRetry, orgId, refreshIntegrationSetups]);

  const openConnectionDrawer = useCallback((record: IntegrationConnection) => {
    if (record.setupRequest) {
      return;
    }
    setErrorMessage(null);
    setConnectionDrawerMode('detail');
    setConnectionDrawerTab('overview');
    setCMDBWorkspaceTab('access');
    setConnectionTestFeedback(null);
    setConnectionRecord(record);
    setConnectionForm(connectionFormFromRecord(record, providerDefinitions.find((item) => item.providerKey === record.provider)));
    setCMDBWritebackPolicy(defaultCMDBWritebackPolicy());
    setCMDBWritebackForm(defaultCMDBWritebackForm());
    setCMDBReconciliationPolicy(defaultCMDBReconciliationPolicy());
    setCMDBReconciliationForm(defaultCMDBReconciliationForm());
    setCMDBMaturityScore(null);
    setCMDBSourceEvidence(null);
    setCMDBDeviceCrosswalk(null);
    setCMDBCrosswalkFilter('all');
    setCMDBSourceRecords([]);
    setCMDBSourceRetention(null);
    setCMDBAccessCheckResult(getPersistedCMDBAccessCheck(record));
    setWebhookProvisioning(null);
    setWebhookError(null);
    setShowWebhookSetup(false);
    setRevealWebhookSecret(false);
    setConnectionDrawerOpen(true);
  }, [providerDefinitions]);

  useEffect(() => {
    if (!connectionDrawerOpen || connectionDrawerMode !== 'detail' || connectionDrawerTab !== 'webhook' || !connectionRecord?.id) {
      return;
    }
    if (
      !supportsWebhookSetup(resolveProviderDefinition(connectionRecord.provider)) ||
      !connectionRecord.config?.webhook?.registrationId ||
      webhookProvisioning ||
      webhookLoading
    ) {
      return;
    }

    setShowWebhookSetup(true);
    void fetchWebhookProvisioning(connectionRecord).catch(() => undefined);
  }, [
    connectionDrawerTab,
    connectionDrawerMode,
    connectionDrawerOpen,
    connectionRecord,
    fetchWebhookProvisioning,
    resolveProviderDefinition,
    webhookLoading,
    webhookProvisioning
  ]);

  useEffect(() => {
    if (!connectionDrawerOpen || !connectionRecord?.id) {
      return;
    }
    const refreshed = connections.find((item) => item.id === connectionRecord.id);
    if (!refreshed) {
      return;
    }
    if (refreshed !== connectionRecord) {
      setConnectionRecord(refreshed);
    }
    const persistedAccessCheck = getPersistedCMDBAccessCheck(refreshed);
    if (persistedAccessCheck && (!cmdbAccessCheckResult || persistedAccessCheck.checkedAt.getTime() !== cmdbAccessCheckResult.checkedAt.getTime())) {
      setCMDBAccessCheckResult(persistedAccessCheck);
    }
  }, [cmdbAccessCheckResult, connectionDrawerOpen, connectionRecord, connections]);

  useEffect(() => {
    const cmdbSupported = supportsCMDBSync(providerDefinitions.find((item) => item.providerKey === connectionForm.provider));
    if (!connectionDrawerOpen || connectionDrawerMode !== 'detail' || connectionDrawerTab !== 'cmdb' || !connectionRecord?.id || !cmdbSupported) {
      return;
    }
    void fetchCMDBReconciliationPolicy(connectionRecord.id);
    void fetchCMDBReconciliationAudit();
    void fetchCMDBSourceEvidence(connectionRecord.id);
    void fetchCMDBDeviceCrosswalk(connectionRecord.id);
  }, [connectionDrawerMode, connectionDrawerOpen, connectionDrawerTab, connectionForm.provider, connectionRecord, fetchCMDBDeviceCrosswalk, fetchCMDBReconciliationAudit, fetchCMDBReconciliationPolicy, fetchCMDBSourceEvidence, providerDefinitions]);

  useEffect(() => {
    if (!orgId || !connectionRecord?.id || !cmdbSyncJob || !['queued', 'running'].includes(cmdbSyncJob.status)) {
      return;
    }
    const timeout = window.setTimeout(() => {
      void integrationsApi.getIntegrationConnectionCMDBSync({
        orgId,
        connectionId: connectionRecord.id!,
        syncRequestId: cmdbSyncJob.id
      }).then(async (job) => {
        setCMDBSyncJob(job);
        if (job.status === 'succeeded') {
          setCMDBSyncBusy(false);
          await Promise.all([
            fetchCMDBSourceEvidence(connectionRecord.id!),
            fetchCMDBDeviceCrosswalk(connectionRecord.id!)
          ]);
        } else if (job.status === 'failed') {
          setCMDBSyncBusy(false);
          setErrorMessage(job.errorSummary || 'CMDB synchronization failed.');
        }
      }).catch(async (error) => {
        setCMDBSyncBusy(false);
        setErrorMessage(await resolveErrorMessage(error, 'Failed to check CMDB synchronization.'));
      });
    }, 1500);
    return () => window.clearTimeout(timeout);
  }, [cmdbSyncJob, connectionRecord?.id, fetchCMDBDeviceCrosswalk, fetchCMDBSourceEvidence, integrationsApi, orgId]);

  useEffect(() => {
    const writebackSupported = supportsCMDBWriteback(providerDefinitions.find((item) => item.providerKey === connectionForm.provider));
    if (!connectionDrawerOpen || connectionDrawerMode !== 'detail' || connectionDrawerTab !== 'cmdb' || !connectionRecord?.id || !writebackSupported) {
      return;
    }
    void fetchCMDBWritebackPolicy(connectionRecord);
    void fetchCMDBPolicyAudit(connectionRecord.id);
  }, [connectionDrawerMode, connectionDrawerOpen, connectionDrawerTab, connectionForm.provider, connectionRecord, fetchCMDBPolicyAudit, fetchCMDBWritebackPolicy, providerDefinitions]);

  const closeConnectionDrawer = useCallback(() => {
    setErrorMessage(null);
    setConnectionDrawerOpen(false);
    setConnectionDrawerTab('overview');
    setCMDBWorkspaceTab('access');
    setConnectionTestFeedback(null);
    setCMDBWritebackPolicy(defaultCMDBWritebackPolicy());
    setCMDBWritebackForm(defaultCMDBWritebackForm());
    setCMDBPolicyAuditEvents([]);
    setCMDBPolicyAuditError(null);
    setCMDBReconciliationAuditEvents([]);
    setCMDBReconciliationAuditError(null);
    setCMDBReconciliationPolicy(defaultCMDBReconciliationPolicy());
    setCMDBReconciliationForm(defaultCMDBReconciliationForm());
    setCMDBMaturityScore(null);
    setCMDBSourceEvidence(null);
    setCMDBSyncJob(null);
    setCMDBSyncBusy(false);
    setCMDBSourceRecords([]);
    setCMDBSourceRetention(null);
    setCMDBDeviceCrosswalk(null);
    setCMDBAccessCheckResult(null);
    setCopyFeedback(null);
    setWebhookProvisioning(null);
    setWebhookError(null);
    setShowWebhookSetup(false);
    setRevealWebhookSecret(false);
  }, []);

  const openFlowLogsResourcePicker = useCallback(
    async (row: CloudTrailSiteRow) => {
      if (!orgId) {
        return;
      }
      setFlowLogsPickerOpen(true);
      setFlowLogsPickerRow(row);
      setFlowLogsResourceOptions(null);
      setFlowLogsResourceLoading(true);
      setSelectedFlowLogVPCIDs([]);
      setSelectedFlowLogTGWIDs([]);
      setAwsQuickCreateURL(null);
      setCloudTrailErrorMessage(null);
      try {
        const payload: AWSFlowLogsResourceOptionsResponse = await cloudIntegrationsApi.orgsOrgIdIntegrationsAwsFlowlogsResourcesGet(
          { orgId, siteId: row.siteId },
          { cache: 'no-store' }
        );
        const resourceOptions = {
          ...payload,
          vpcs: listResponseItems(payload.vpcs),
          transitGateways: listResponseItems(payload.transitGateways)
        };
        setFlowLogsResourceOptions(resourceOptions);
        setSelectedFlowLogVPCIDs(resourceOptions.vpcs.map((item) => item.id).filter(Boolean));
        setSelectedFlowLogTGWIDs(resourceOptions.transitGateways.map((item) => item.id).filter(Boolean));
      } catch (error) {
        setCloudTrailErrorMessage(await resolveErrorMessage(error, 'Failed to load selectable Flow Logs resources.'));
      } finally {
        setFlowLogsResourceLoading(false);
      }
    },
    [cloudIntegrationsApi, orgId]
  );

  const closeFlowLogsResourcePicker = useCallback(() => {
    setFlowLogsPickerOpen(false);
    setFlowLogsPickerRow(null);
    setFlowLogsResourceOptions(null);
    setFlowLogsResourceLoading(false);
    setSelectedFlowLogVPCIDs([]);
    setSelectedFlowLogTGWIDs([]);
  }, []);

  const toggleSelectedFlowLogResource = useCallback((kind: 'vpc' | 'tgw', id: string) => {
    const setter = kind === 'vpc' ? setSelectedFlowLogVPCIDs : setSelectedFlowLogTGWIDs;
    setter((prev) => (prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id].sort()));
  }, []);

  const openCloudWatchLogsPicker = useCallback((row: CloudTrailSiteRow) => {
    setCloudWatchLogsPickerOpen(true);
    setCloudWatchLogsPickerRow(row);
    setCloudWatchLogsInput(row.logGroups.join('\n'));
    setCloudWatchLogsAPIKey(null);
    setCloudTrailErrorMessage(null);
  }, []);

  const closeCloudWatchLogsPicker = useCallback(() => {
    setCloudWatchLogsPickerOpen(false);
    setCloudWatchLogsPickerRow(null);
    setCloudWatchLogsInput('');
  }, []);

  const createAWSFlowLogsIntegration = useCallback(
    async (payload: AWSFlowLogsIntegrationCreate): Promise<AWSFlowLogsLaunchResponse> => {
      if (!orgId) {
        throw new Error('Select an organization first.');
      }
      return await cloudIntegrationsApi.orgsOrgIdIntegrationsAwsFlowlogsPost(
        { orgId, aWSFlowLogsIntegrationCreate: payload },
        { cache: 'no-store' }
      ) as AWSFlowLogsLaunchResponse;
    },
    [cloudIntegrationsApi, orgId]
  );

  const createAWSCloudWatchLogsIntegration = useCallback(
    async (payload: AWSCloudWatchLogsIntegrationCreate): Promise<AWSCloudWatchLogsLaunchResponse> => {
      if (!orgId) {
        throw new Error('Select an organization first.');
      }
      return await cloudIntegrationsApi.orgsOrgIdIntegrationsAwsCloudwatchLogsPost(
        { orgId, aWSCloudWatchLogsIntegrationCreate: payload },
        { cache: 'no-store' }
      ) as AWSCloudWatchLogsLaunchResponse;
    },
    [cloudIntegrationsApi, orgId]
  );

  const enableAWSCloudWatchLogsIntegration = useCallback(
    async (connectionId: string): Promise<AWSCloudWatchLogsLaunchResponse> => {
      if (!orgId) {
        throw new Error('Select an organization first.');
      }
      return await cloudIntegrationsApi.orgsOrgIdIntegrationsAwsCloudwatchLogsConnectionIdEnablePost(
        { orgId, connectionId },
        { cache: 'no-store' }
      ) as AWSCloudWatchLogsLaunchResponse;
    },
    [cloudIntegrationsApi, orgId]
  );

  const openBackendQuickCreateURL = useCallback((popup: Window | null, url?: string) => {
    const quickCreateUrl = typeof url === 'string' ? url.trim() : '';
    if (!quickCreateUrl) {
      popup?.close();
      setCloudTrailErrorMessage('Backend response missing AWS CloudFormation quick-create URL.');
      return false;
    }
    if (popup) {
      if (!navigatePendingTab(popup, quickCreateUrl)) {
        popup.close();
        setCloudTrailErrorMessage('Popup was blocked. Allow popups for this site and try again.');
        return false;
      }
      return true;
    }
    window.open(quickCreateUrl, '_blank', 'noopener,noreferrer');
    return true;
  }, []);

  const onboardCloudTrail = useCallback(
    async (row: CloudTrailSiteRow, selectedResources?: { vpcIds: string[]; transitGatewayIds: string[] }, selectedLogGroups?: string[]) => {
      if (!orgId) {
        return;
      }
      const needsQuickCreate = selectedCloudSignal !== 'cloudwatch-logs';
      const popup = needsQuickCreate ? openPendingTab() : null;
      if (needsQuickCreate && !popup) {
        setCloudTrailErrorMessage('Popup was blocked. Allow popups for this site and try again.');
        return;
      }
      setCloudTrailActionBusy(row.siteId);
      setCloudTrailErrorMessage(null);
      setAwsQuickCreateURL(null);
      setCloudWatchLogsAPIKey(null);
      try {
        let payload: AWSCloudLaunchResponse;
        if (selectedCloudSignal === 'flowlogs') {
          if (!selectedResources) {
            throw new Error('Select at least one VPC or transit gateway.');
          }
          payload = (await createAWSFlowLogsIntegration({
            siteId: row.siteId,
            vpcIds: selectedResources.vpcIds,
            transitGatewayIds: selectedResources.transitGatewayIds
          })) as AWSFlowLogsIntegrationOnboardResponse;
        } else if (selectedCloudSignal === 'cloudwatch-logs') {
          if (!selectedLogGroups || selectedLogGroups.length === 0) {
            throw new Error('Enter at least one log group.');
          }
          payload = await createAWSCloudWatchLogsIntegration({
            siteId: row.siteId,
            logGroups: selectedLogGroups
          });
        } else {
          payload = await cloudIntegrationsApi.orgsOrgIdIntegrationsAwsCloudtrailPost({
            orgId,
            cloudTrailIntegrationCreate: {
              siteId: row.siteId
            }
          });
        }
        const nextRow = mergeCloudTrailIntegration(row, payload.integration);
        setCloudTrailDrawerRecord(nextRow);
        setCloudTrailDrawerOpen(true);
        await fetchCloudTrailIntegrations();
        const quickCreateUrl = payload.quickCreateUrl ?? null;
        setAwsQuickCreateURL(quickCreateUrl);
        setCloudWatchLogsAPIKey(payload.apiKey ?? null);
        if (needsQuickCreate) {
          openBackendQuickCreateURL(popup, quickCreateUrl ?? undefined);
        }
        if (selectedCloudSignal === 'flowlogs') {
          closeFlowLogsResourcePicker();
        } else if (selectedCloudSignal === 'cloudwatch-logs') {
          closeCloudWatchLogsPicker();
        }
      } catch (error) {
        popup?.close();
        setCloudTrailErrorMessage(await resolveErrorMessage(error, `Failed to onboard ${selectedCloudSignalLabel} integration.`));
      } finally {
        setCloudTrailActionBusy(null);
      }
    },
    [
      closeCloudWatchLogsPicker,
      closeFlowLogsResourcePicker,
      cloudIntegrationsApi,
      createAWSCloudWatchLogsIntegration,
      createAWSFlowLogsIntegration,
      fetchCloudTrailIntegrations,
      mergeCloudTrailIntegration,
      openBackendQuickCreateURL,
      orgId,
      selectedCloudSignal,
      selectedCloudSignalLabel
    ]
  );

  const enableCloudTrail = useCallback(
    async (row: CloudTrailSiteRow) => {
      if (!orgId || !row.integration?.id) {
        return;
      }
      const connectionId = row.integration.id;
      const needsQuickCreate = selectedCloudSignal !== 'cloudwatch-logs';
      const popup = needsQuickCreate ? openPendingTab() : null;
      if (needsQuickCreate && !popup) {
        setCloudTrailErrorMessage('Popup was blocked. Allow popups for this site and try again.');
        return;
      }
      setCloudTrailActionBusy(row.siteId);
      setCloudTrailErrorMessage(null);
      setAwsQuickCreateURL(null);
      setCloudWatchLogsAPIKey(null);
      try {
        const payload: AWSCloudLaunchResponse =
          selectedCloudSignal === 'flowlogs'
            ? ((await cloudIntegrationsApi.orgsOrgIdIntegrationsAwsFlowlogsConnectionIdEnablePost({
                orgId,
                connectionId
              })) as AWSFlowLogsIntegrationEnableResponse)
            : selectedCloudSignal === 'cloudwatch-logs'
              ? await enableAWSCloudWatchLogsIntegration(connectionId)
            : await cloudIntegrationsApi.orgsOrgIdIntegrationsAwsCloudtrailConnectionIdEnablePost({
                orgId,
                connectionId
              });
        const nextRow = mergeCloudTrailIntegration(row, payload.integration);
        setCloudTrailDrawerRecord(nextRow);
        setCloudTrailDrawerOpen(true);
        await fetchCloudTrailIntegrations();
        const quickCreateUrl = payload.quickCreateUrl ?? null;
        setAwsQuickCreateURL(quickCreateUrl);
        setCloudWatchLogsAPIKey(payload.apiKey ?? null);
        if (needsQuickCreate) {
          openBackendQuickCreateURL(popup, quickCreateUrl ?? undefined);
        }
      } catch (error) {
        popup?.close();
        setCloudTrailErrorMessage(await resolveErrorMessage(error, `Failed to enable ${selectedCloudSignalLabel} integration.`));
      } finally {
        setCloudTrailActionBusy(null);
      }
    },
    [cloudIntegrationsApi, enableAWSCloudWatchLogsIntegration, fetchCloudTrailIntegrations, mergeCloudTrailIntegration, openBackendQuickCreateURL, orgId, selectedCloudSignal, selectedCloudSignalLabel]
  );

  const confirmFlowLogsResourceSelection = useCallback(async () => {
    if (!flowLogsPickerRow) {
      return;
    }
    if (selectedFlowLogVPCIDs.length + selectedFlowLogTGWIDs.length === 0) {
      setCloudTrailErrorMessage('Select at least one VPC or transit gateway.');
      return;
    }
    await onboardCloudTrail(flowLogsPickerRow, {
      vpcIds: selectedFlowLogVPCIDs,
      transitGatewayIds: selectedFlowLogTGWIDs
    });
  }, [flowLogsPickerRow, onboardCloudTrail, selectedFlowLogTGWIDs, selectedFlowLogVPCIDs]);

  const confirmCloudWatchLogsSelection = useCallback(async () => {
    if (!cloudWatchLogsPickerRow) {
      return;
    }
    const logGroups = parseLogGroupInput(cloudWatchLogsInput);
    if (logGroups.length === 0) {
      setCloudTrailErrorMessage('Enter at least one log group.');
      return;
    }
    await onboardCloudTrail(cloudWatchLogsPickerRow, undefined, logGroups);
  }, [cloudWatchLogsInput, cloudWatchLogsPickerRow, onboardCloudTrail]);

  const disableCloudTrail = useCallback(
    async (row: CloudTrailSiteRow) => {
      if (!orgId || !row.integration?.id) {
        return;
      }
      const connectionId = row.integration.id;
      setCloudTrailActionBusy(row.siteId);
      setCloudTrailErrorMessage(null);
      try {
        const payload: AWSCloudLogIntegration =
          selectedCloudSignal === 'flowlogs'
            ? ((await cloudIntegrationsApi.orgsOrgIdIntegrationsAwsFlowlogsConnectionIdDisablePost({
                orgId,
                connectionId
              })) as AWSFlowLogsIntegration)
            : selectedCloudSignal === 'cloudwatch-logs'
              ? await cloudIntegrationsApi.orgsOrgIdIntegrationsAwsCloudwatchLogsConnectionIdDisablePost({
                  orgId,
                  connectionId
                })
            : await cloudIntegrationsApi.orgsOrgIdIntegrationsAwsCloudtrailConnectionIdDisablePost({
                orgId,
                connectionId
              });
        const nextRow = mergeCloudTrailIntegration(row, payload);
        setCloudTrailDrawerRecord(nextRow);
        setCloudTrailDrawerOpen(true);
        await fetchCloudTrailIntegrations();
      } catch (error) {
        setCloudTrailErrorMessage(await resolveErrorMessage(error, `Failed to disable ${selectedCloudSignalLabel} integration.`));
      } finally {
        setCloudTrailActionBusy(null);
      }
    },
    [cloudIntegrationsApi, fetchCloudTrailIntegrations, mergeCloudTrailIntegration, orgId, selectedCloudSignal, selectedCloudSignalLabel]
  );

  const deleteCloudTrail = useCallback(
    async (row: CloudTrailSiteRow, options?: { successMessage?: string }) => {
      if (!orgId || !row.integration?.id) {
        return;
      }
      setCloudTrailActionBusy(row.siteId);
      setCloudTrailErrorMessage(null);
      try {
        if (selectedCloudSignal === 'flowlogs') {
          await cloudIntegrationsApi.orgsOrgIdIntegrationsAwsFlowlogsConnectionIdDelete({
            orgId,
            connectionId: row.integration.id
          });
        } else if (selectedCloudSignal === 'cloudwatch-logs') {
          await cloudIntegrationsApi.orgsOrgIdIntegrationsAwsCloudwatchLogsConnectionIdDelete({
            orgId,
            connectionId: row.integration.id
          });
        } else {
          await cloudIntegrationsApi.orgsOrgIdIntegrationsAwsCloudtrailConnectionIdDelete({
            orgId,
            connectionId: row.integration.id
          });
        }
        setCloudTrailItems((current) =>
          current.map((item) => (item.siteId === row.siteId ? mergeCloudTrailIntegration(item, null) : item))
        );
        setCloudTrailDrawerOpen(false);
        setCloudTrailDrawerRecord(null);
        setDeleteDialog(null);
        setDeleteToast({
          tone: 'success',
          message: options?.successMessage ?? `${selectedCloudSignalLabel} integration deleted successfully.`
        });
        await fetchCloudTrailIntegrations();
      } catch (error) {
        const message = await resolveErrorMessage(error, `Failed to delete ${selectedCloudSignalLabel} integration.`);
        setCloudTrailErrorMessage(message);
        setDeleteToast({ tone: 'error', message });
      } finally {
        setCloudTrailActionBusy(null);
      }
    },
    [cloudIntegrationsApi, fetchCloudTrailIntegrations, mergeCloudTrailIntegration, orgId, selectedCloudSignal, selectedCloudSignalLabel]
  );

  const openConnectionDeleteDialog = useCallback((connection: IntegrationConnection) => {
    const name = connection.name?.trim() || connection.provider || 'this integration';
    setDeleteDialog({
      kind: 'connection',
      connection,
      title: 'Delete integration?',
      description: `Delete ${name}? This permanently removes the integration connection.`,
      successMessage: `${name} deleted successfully.`
    });
  }, []);

  const openCloudDeleteDialog = useCallback(
    (row: CloudTrailSiteRow) => {
      setDeleteDialog({
        kind: 'cloud',
        row,
        title: `Delete ${selectedCloudSignalLabel} integration?`,
        description: `Delete the ${selectedCloudSignalLabel} integration for site ${row.siteName}? This removes the current integration setup for this site.`,
        successMessage: `${selectedCloudSignalLabel} integration for ${row.siteName} deleted successfully.`
      });
    },
    [selectedCloudSignalLabel]
  );

  const openTargetDeleteDialog = useCallback((target: InteractionTarget) => {
    const name = target.name?.trim() || target.externalTargetId?.trim() || 'this interaction target';
    setDeleteDialog({
      kind: 'target',
      target,
      title: 'Delete interaction target?',
      description: `Delete ${name}? This permanently removes the interaction target.`,
      successMessage: `${name} deleted successfully.`
    });
  }, []);

  const openRuleDeleteDialog = useCallback((rule: InteractionRoutingRule) => {
    const name = rule.name?.trim() || rule.targetRef?.trim() || 'this routing rule';
    setDeleteDialog({
      kind: 'rule',
      rule,
      title: 'Delete routing rule?',
      description: `Delete ${name}? This permanently removes the routing rule.`,
      successMessage: `${name} deleted successfully.`
    });
  }, []);

  const closeDeleteDialog = useCallback(() => {
    setDeleteDialog(null);
  }, []);

  const openCloudTrailDetailDrawer = useCallback((record: CloudTrailSiteRow) => {
    setCloudTrailDrawerRecord(record);
    setCloudTrailDrawerOpen(true);
    setCloudWatchLogsAPIKey(null);
  }, []);

  const closeCloudTrailDrawer = useCallback(() => {
    setCloudTrailDrawerOpen(false);
    setCloudTrailDrawerRecord(null);
    setCloudWatchLogsAPIKey(null);
  }, []);

  const submitTarget = useCallback(async () => {
    if (!orgId) {
      return;
    }
    setSavingTarget(true);
    setErrorMessage(null);
    try {
      const payload = {
        name: targetForm.name.trim(),
        provider: targetForm.provider,
        connectionId: targetForm.connectionId.trim(),
        targetType: targetForm.targetType.trim(),
        externalTargetId: targetForm.externalTargetId.trim(),
        threadBindingId: targetForm.threadBindingId.trim(),
        isDefault: targetForm.isDefault,
        labels: splitCsv(targetForm.labels),
        meta: parseJsonInput('Target meta', targetForm.metaJson)
      };
      const nextRecord = mapInteractionTargetRecord(
        targetDrawerMode === 'create'
          ? await integrationsApi.createInteractionTarget({ orgId, interactionTargetCreateRequest: payload }, { cache: 'no-store' })
          : await integrationsApi.updateInteractionTarget({ orgId, targetId: targetRecord?.id ?? '', body: payload }, { cache: 'no-store' })
      );
      setTargetRecord(nextRecord);
      setTargetDrawerMode('detail');
      setTargetForm(targetFormFromRecord(nextRecord));
      await fetchTargets();
    } catch (error) {
      setErrorMessage(await resolveErrorMessage(error, 'Failed to save interaction target.'));
    } finally {
      setSavingTarget(false);
    }
  }, [fetchTargets, integrationsApi, orgId, targetDrawerMode, targetForm, targetRecord?.id]);

  const deleteTarget = useCallback(
    async (target: InteractionTarget, options?: { successMessage?: string }) => {
      if (!orgId || !target.id) {
        return;
      }
      setRowActionBusy(target.id);
      setErrorMessage(null);
      try {
        await integrationsApi.deleteInteractionTarget({ orgId, targetId: target.id }, { cache: 'no-store' });
        if (targetRecord?.id === target.id) {
          setTargetDrawerOpen(false);
          setTargetRecord(null);
        }
        setTargets((current) => current.filter((item) => item.id !== target.id));
        setDeleteDialog(null);
        setDeleteToast({
          tone: 'success',
          message: options?.successMessage ?? 'Interaction target deleted successfully.'
        });
        await fetchTargets();
      } catch (error) {
        const message = await resolveErrorMessage(error, 'Failed to delete interaction target.');
        setErrorMessage(message);
        setDeleteToast({ tone: 'error', message });
      } finally {
        setRowActionBusy(null);
      }
    },
    [fetchTargets, integrationsApi, orgId, targetRecord?.id]
  );

  const submitRule = useCallback(async () => {
    if (!orgId) {
      return;
    }
    setSavingRule(true);
    setErrorMessage(null);
    try {
      const payload = {
        name: ruleForm.name.trim(),
        provider: ruleForm.provider,
        connectionId: ruleForm.connectionId.trim(),
        targetRef: ruleForm.targetRef.trim(),
        assignmentGroup: ruleForm.assignmentGroup.trim(),
        category: ruleForm.category.trim(),
        priority: ruleForm.priority.trim(),
        targetOverrideRef: ruleForm.targetOverrideRef.trim(),
        enabled: ruleForm.enabled,
        conditions: parseJsonInput('Rule conditions', ruleForm.conditionsJson)
      };
      const nextRecord = mapInteractionRoutingRuleRecord(
        ruleDrawerMode === 'create'
          ? await integrationsApi.createInteractionRoutingRule({ orgId, interactionRoutingRuleCreateRequest: payload }, { cache: 'no-store' })
          : await integrationsApi.updateInteractionRoutingRule({ orgId, ruleId: ruleRecord?.id ?? '', body: payload }, { cache: 'no-store' })
      );
      setRuleRecord(nextRecord);
      setRuleDrawerMode('detail');
      setRuleForm(ruleFormFromRecord(nextRecord));
      await fetchRoutingRules();
    } catch (error) {
      setErrorMessage(await resolveErrorMessage(error, 'Failed to save routing rule.'));
    } finally {
      setSavingRule(false);
    }
  }, [fetchRoutingRules, integrationsApi, orgId, ruleDrawerMode, ruleForm, ruleRecord?.id]);

  const deleteRule = useCallback(
    async (rule: InteractionRoutingRule, options?: { successMessage?: string }) => {
      if (!orgId || !rule.id) {
        return;
      }
      setRowActionBusy(rule.id);
      setErrorMessage(null);
      try {
        await integrationsApi.deleteInteractionRoutingRule({ orgId, ruleId: rule.id }, { cache: 'no-store' });
        if (ruleRecord?.id === rule.id) {
          setRuleDrawerOpen(false);
          setRuleRecord(null);
        }
        setRoutingRules((current) => current.filter((item) => item.id !== rule.id));
        setDeleteDialog(null);
        setDeleteToast({
          tone: 'success',
          message: options?.successMessage ?? 'Routing rule deleted successfully.'
        });
        await fetchRoutingRules();
      } catch (error) {
        const message = await resolveErrorMessage(error, 'Failed to delete routing rule.');
        setErrorMessage(message);
        setDeleteToast({ tone: 'error', message });
      } finally {
        setRowActionBusy(null);
      }
    },
    [fetchRoutingRules, integrationsApi, orgId, ruleRecord?.id]
  );

  const confirmDeleteDialog = useCallback(async () => {
    if (!deleteDialog) {
      return;
    }
    if (deleteDialog.kind === 'connection') {
      await deleteConnection(deleteDialog.connection, { successMessage: deleteDialog.successMessage });
      return;
    }
    if (deleteDialog.kind === 'cloud') {
      await deleteCloudTrail(deleteDialog.row, { successMessage: deleteDialog.successMessage });
      return;
    }
    if (deleteDialog.kind === 'target') {
      await deleteTarget(deleteDialog.target, { successMessage: deleteDialog.successMessage });
      return;
    }
    await deleteRule(deleteDialog.rule, { successMessage: deleteDialog.successMessage });
  }, [deleteCloudTrail, deleteConnection, deleteDialog, deleteRule, deleteTarget]);

  const filterActive = useMemo(
    () => filters.status !== 'all' || filters.provider !== 'all' || filters.q.trim() !== '',
    [filters]
  );

  const pendingSetupRows = useMemo<IntegrationConnection[]>(() => {
    return Object.values(integrationSetups)
      .filter(integrationSetupIsPending)
      .map((setup) => {
        const definition = providerDefinitions.find((item) => normalizeProvider(item.providerKey) === setup.provider);
        const name = definition?.displayName ?? setup.provider;
        return { id: `setup-${setup.provider}-${setup.id}`, name, provider: setup.provider, status: 'pending', setupRequest: setup };
      });
  }, [integrationSetups, providerDefinitions]);

  const paginationMeta = useMemo(() => {
    const totalCount = connectionsTotal;
    const pageCount = Math.max(1, Math.ceil(totalCount / pageSize));
    return {
      totalCount,
      pageCount,
      hasPrevPage: page > 1,
      hasNextPage: page < pageCount,
      exactTotal: totalCount
    };
  }, [connectionsTotal, page, pageSize]);

  useEffect(() => {
    if (page > paginationMeta.pageCount) {
      setPage(paginationMeta.pageCount);
    }
  }, [page, paginationMeta.pageCount]);

  const cloudTrailPaginationMeta = useMemo(() => {
    const totalCount = cloudTrailItems.length;
    return {
      totalCount,
      pageCount: totalCount === 0 ? 1 : 1,
      hasPrevPage: false,
      hasNextPage: false,
      exactTotal: totalCount
    };
  }, [cloudTrailItems]);

  const selectedCloudProviderOption = useMemo(
    () => cloudProviderOptions.find((option) => option.value === selectedCloudProvider) ?? null,
    [selectedCloudProvider]
  );

  const cloudSignalSupported = (selectedCloudProvider === 'aws' && (selectedCloudSignal === 'cloudtrail' || selectedCloudSignal === 'flowlogs' || selectedCloudSignal === 'cloudwatch-logs')) || (selectedCloudProvider === 'azure' && selectedCloudSignal === 'resource-changes');
  const cloudSelectionReady = Boolean(selectedCloudProvider && selectedCloudSignal);
  const deleteDialogBusy =
    deleteDialog?.kind === 'connection'
      ? rowActionBusy === (deleteDialog.connection.id ?? '')
      : deleteDialog?.kind === 'cloud'
        ? cloudTrailActionBusy === deleteDialog.row.siteId
        : deleteDialog?.kind === 'target'
          ? rowActionBusy === (deleteDialog.target.id ?? '')
          : deleteDialog?.kind === 'rule'
            ? rowActionBusy === (deleteDialog.rule.id ?? '')
            : false;

  const providerByKey = useMemo(
    () => Object.fromEntries(providerDefinitions.map((item) => [item.providerKey, item])),
    [providerDefinitions]
  );
  const interactionConnections = useMemo(
    () =>
      connections
        .filter((item) => isInteractionDefinition(providerByKey[item.provider ?? '']))
        .sort((left, right) => (left.name ?? '').localeCompare(right.name ?? '')),
    [connections, providerByKey]
  );
  const currentConnectionProvider = connectionForm.provider;
  const currentConnectionDefinition = providerByKey[currentConnectionProvider];
  const ticketingFlow = isTicketingDefinition(currentConnectionDefinition);
  const webhookFlow = supportsWebhookSetup(currentConnectionDefinition);
  const cmdbSyncFlow = supportsCMDBSync(currentConnectionDefinition);
  const cmdbWritebackFlow = supportsCMDBWriteback(currentConnectionDefinition);
  const currentConnectionFields = useMemo(
    () => (currentConnectionDefinition?.fields ?? []).filter((field) => isIntegrationConfigFieldVisible(field, connectionForm.values)),
    [connectionForm.values, currentConnectionDefinition]
  );

  const interactionProviderDefinitions = useMemo(
    () => providerDefinitions.filter((item) => item.supportsTargets || item.supportsRouting),
    [providerDefinitions]
  );
  const defaultInteractionProvider = interactionProviderDefinitions[0]?.providerKey ?? '';

  const targetColumns = useMemo<Array<ListWorkspaceColumn<InteractionTarget>>>(
    () => [
      {
        key: 'name',
        header: 'Target',
        render: (row) => (
          <div>
            <strong>{row.name ?? 'Unnamed target'}</strong>
            <div style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>{row.provider ?? '—'} · {row.targetType ?? '—'}</div>
          </div>
        )
      },
      {
        key: 'externalTargetId',
        header: 'Destination',
        render: (row) => row.externalTargetId ?? '—'
      },
      {
        key: 'connectionId',
        header: 'Connection',
        render: (row) => {
          const match = interactionConnections.find((item) => item.id === row.connectionId);
          return match?.name ?? row.connectionId ?? '—';
        }
      },
      {
        key: 'updatedAt',
        header: 'Updated',
        render: (row) => formatDateTime(row.updatedAt ?? row.createdAt ?? null)
      }
    ],
    [interactionConnections]
  );

  const ruleColumns = useMemo<Array<ListWorkspaceColumn<InteractionRoutingRule>>>(
    () => [
      {
        key: 'name',
        header: 'Rule',
        render: (row) => (
          <div>
            <strong>{row.name ?? 'Unnamed rule'}</strong>
            <div style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>{row.provider ?? '—'} · {row.enabled ? 'Enabled' : 'Disabled'}</div>
          </div>
        )
      },
      {
        key: 'targetRef',
        header: 'Target Ref',
        render: (row) => row.targetRef ?? '—'
      },
      {
        key: 'conditions',
        header: 'Match',
        render: (row) => [row.assignmentGroup, row.category, row.priority].filter(Boolean).join(' · ') || 'Custom JSON'
      },
      {
        key: 'updatedAt',
        header: 'Updated',
        render: (row) => formatDateTime(row.updatedAt ?? row.createdAt ?? null)
      }
    ],
    []
  );

  const connectionColumns = useMemo<Array<ListWorkspaceColumn<IntegrationConnection>>>(
    () => [
      {
        key: 'name',
        header: 'Name',
        render: (row) => (
          <div>
            <strong>{row.name ?? 'Untitled connection'}</strong>
            <div style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
              {row.setupRequest
                ? 'Workspace setup is awaiting administrator approval.'
                : row.provider ?? '—'}
            </div>
          </div>
        )
      },
      {
        key: 'status',
        header: 'Status',
        render: (row) => {
          if (row.setupRequest) {
            return <span className="ui-badge ui-badge--warning">Waiting for approval</span>;
          }
          const setup = integrationSetups[normalizeProvider(row.provider) ?? ''];
          if (setup?.status === 'connected' && setup.connectionId === row.id) {
            return <span className="ui-badge ui-badge--success">Connected</span>;
          }
          const badge = formatConnectionStatusBadge(row.status ?? null);
          return <span className={badge.className}>{badge.label}</span>;
        }
      },
      {
        key: 'updatedAt',
        header: 'Updated',
        render: (row) => row.setupRequest ? 'Awaiting administrator review' : formatDateTime(row.updatedAt ?? row.createdAt ?? null)
      },
      {
        key: 'lastTestedAt',
        header: 'Last tested',
        render: (row) => row.setupRequest ? '—' : formatDateTime(row.lastTestedAt ?? null)
      }
    ],
    [integrationSetups]
  );

  const cloudTrailColumns = useMemo<Array<ListWorkspaceColumn<CloudTrailSiteRow>>>(() => {
    return [
      {
        key: 'name',
        width: '34%',
        header: 'Site',
        render: (row) => (
          <div>
            <strong>{row.siteName}</strong>
            <div style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
              {row.integration?.name ?? `${selectedCloudSignalLabel} not onboarded`}
            </div>
          </div>
        )
      },
      {
        key: 'accountRegion',
        width: '20%',
        header: 'ID / Region',
        render: (row) => `${row.awsAccountId} / ${row.region}`
      },
      {
        key: 'status',
        width: '18%',
        header: 'Status',
        render: (row) => {
          const badge = toCloudTrailStatusBadge(row.status);
          return <span className={badge.className}>{badge.label}</span>;
        }
      },
      {
        key: 'updatedAt',
        width: '18%',
        header: 'Updated',
        render: (row) => formatDateTime(row.updatedAt ?? row.integration?.createdAt ?? null)
      }
    ];
  }, [selectedCloudSignalLabel, toCloudTrailStatusBadge]);

  const azureResourceChangesColumns = useMemo<Array<ListWorkspaceColumn<AzureResourceChangesRow>>>(() => [
    { key: 'subscription', header: 'Azure subscription', render: (row) => <><strong>{row.siteName}</strong><div style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>{row.subscriptionId}</div></> },
    { key: 'status', header: 'Status', render: (row) => <span className={row.integration?.status === 'enabled' ? 'ui-badge ui-badge--success' : row.integration?.status === 'disabled' ? 'ui-badge ui-badge--warning' : 'ui-badge'}>{row.integration?.status === 'enabled' ? 'Enabled' : row.integration?.status === 'disabled' ? 'Disabled' : 'Not onboarded'}</span> },
    { key: 'updated', header: 'Updated', render: (row) => formatDateTime(row.integration?.updatedAt ?? row.integration?.createdAt ?? null) }
  ], []);

  const runAzureResourceChangeAction = useCallback(async (row: AzureResourceChangesRow, action: 'create' | 'enable' | 'disable' | 'delete') => {
    if (!orgId || (action === 'create' && !row.siteId) || (action !== 'create' && !row.integration?.id)) return;
    setAzureResourceChangesBusy(row.subscriptionId);
    setCloudTrailErrorMessage(null);
    try {
      const headers = await cloneHeadersWithAuth({ Accept: 'application/json', 'Content-Type': 'application/json' });
      const connectionId = row.integration?.id ? `/${encodeURIComponent(row.integration.id)}` : '';
      const endpoint = action === 'create'
        ? buildOrgUrl(basePath, orgId, '/integrations/azure/resource-changes')
        : buildOrgUrl(basePath, orgId, `/integrations/azure/resource-changes${connectionId}${action === 'enable' || action === 'disable' ? `/${action}` : ''}`);
      const response = await fetchWithAuthRetry(endpoint, { method: action === 'delete' ? 'DELETE' : 'POST', headers, cache: 'no-store', body: action === 'create' ? JSON.stringify({ siteId: row.siteId }) : undefined });
      if (!response.ok) throw new ApiResponseError(response, `Failed to ${action} Azure resource-change integration.`);
      if (action === 'create' || action === 'enable') {
        const payload = (await response.json()) as { apiKeySecret?: string; launchUrl?: string };
        if (payload.apiKeySecret && payload.launchUrl) setAzureResourceChangesSecret({ subscriptionId: row.subscriptionId, secret: payload.apiKeySecret, launchUrl: payload.launchUrl });
      }
      await fetchAzureResourceChanges();
    } catch (error) {
      setCloudTrailErrorMessage(await resolveErrorMessage(error, `Failed to ${action} Azure resource-change integration.`));
    } finally {
      setAzureResourceChangesBusy(null);
    }
  }, [basePath, fetchAzureResourceChanges, fetchWithAuthRetry, orgId]);

  const renderAzureResourceChangesActions = useCallback((row: AzureResourceChangesRow) => {
    const busy = azureResourceChangesBusy === row.subscriptionId;
    const status = row.integration?.status;
    return <div className="cloud-integrations-row-actions cloud-integrations-row-actions--nowrap" onClick={(event) => event.stopPropagation()}>
      {!row.integration ? <button type="button" className="cloud-integrations-row-actions__button" disabled={busy || !row.siteId} onClick={() => void runAzureResourceChangeAction(row, 'create')}>{busy ? 'Working…' : 'Set up'}</button> : <>
        <button type="button" className="cloud-integrations-row-actions__button" disabled={busy} onClick={() => void runAzureResourceChangeAction(row, status === 'enabled' ? 'disable' : 'enable')}>{busy ? 'Working…' : status === 'enabled' ? 'Disable' : 'Enable'}</button>
        <button type="button" className="cloud-integrations-row-actions__button is-danger" disabled={busy} onClick={() => void runAzureResourceChangeAction(row, 'delete')}>Delete</button>
      </>}
    </div>;
  }, [azureResourceChangesBusy, runAzureResourceChangeAction]);

  const cloudTrailHeaderStatus = useMemo(
    () =>
      cloudTrailItems.length > 0 ? (
        <span className="cloud-integrations-list__status">
          {cloudTrailItems.length} site{cloudTrailItems.length === 1 ? '' : 's'}
        </span>
      ) : undefined,
    [cloudTrailItems]
  );

  const downloadTeamsAppPackage = useCallback(
    async (connection: IntegrationConnection) => {
      const connectionID = connection.id?.trim();
      if (!orgId || !connectionID) {
        return;
      }
      setDownloadingTeamsPackageConnectionID(connectionID);
      setErrorMessage(null);
      try {
        const headers = await cloneHeadersWithAuth({ Accept: 'application/zip' });
        const response = await fetchWithAuthRetry(
          buildOrgUrl(basePath, orgId, `/integrations/connections/${encodeURIComponent(connectionID)}/teams-app-package`),
          { headers }
        );
        if (!response.ok) {
          throw new Error(response.status === 404 ? 'The Teams connection or app package is no longer available.' : 'Unable to download the Teams app package.');
        }
        const blob = await response.blob();
        const objectURL = URL.createObjectURL(blob);
        try {
          const anchor = document.createElement('a');
          anchor.href = objectURL;
          anchor.download = 'reap-teams-app-v0.2.1.zip';
          document.body.appendChild(anchor);
          anchor.click();
          anchor.remove();
        } finally {
          URL.revokeObjectURL(objectURL);
        }
      } catch (error) {
        setErrorMessage(await resolveErrorMessage(error, 'Unable to download the Teams app package.'));
      } finally {
        setDownloadingTeamsPackageConnectionID((current) => (current === connectionID ? null : current));
      }
    },
    [basePath, fetchWithAuthRetry, orgId]
  );

  const renderConnectionRowActions = useCallback(
    (row: IntegrationConnection) => {
      if (row.setupRequest) {
        const provider = row.setupRequest.provider;
        const continuing = startingBootstrapProviderKey === provider;
        const cancelling = cancellingSetupProviderKey === provider;
        return (
          <div className="cloud-integrations-row-actions cloud-integrations-row-actions--nowrap">
            <button
              type="button"
              className="shortcut-button"
              disabled={continuing || cancelling}
              onClick={(event) => {
                event.stopPropagation();
                void startProviderBootstrap({
                  providerKey: provider,
                  displayName: row.name ?? provider,
                  bootstrap: { type: 'oauth_continue', method: 'POST', path: `/integrations/setup/${encodeURIComponent(provider)}/actions` }
                } as ProviderDefinition);
              }}
            >
              {continuing ? 'Opening…' : 'Continue setup'}
            </button>
            <button
              type="button"
              className="shortcut-button"
              disabled={continuing || cancelling}
              onClick={(event) => {
                event.stopPropagation();
                void cancelIntegrationSetup(row.setupRequest!);
              }}
            >
              {cancelling ? 'Deleting…' : 'Delete request'}
            </button>
          </div>
        );
      }
      if (!row.id) {
        return null;
      }
      const busy = rowActionBusy === row.id;
      const isTeamsConnection = normalizeProvider(row.provider) === 'teams';
      const downloadingTeamsPackage = downloadingTeamsPackageConnectionID === row.id;
      const canTest = supportsConnectionTest(providerByKey[normalizeProvider(row.provider) ?? '']);
      return (
        <div className="cloud-integrations-row-actions cloud-integrations-row-actions--nowrap">
          {isTeamsConnection ? (
            <button
              type="button"
              className="shortcut-button"
              disabled={busy || downloadingTeamsPackage}
              onClick={(event) => {
                event.stopPropagation();
                void downloadTeamsAppPackage(row);
              }}
            >
              {downloadingTeamsPackage ? 'Downloading…' : 'Download app ZIP'}
            </button>
          ) : null}
          {canTest ? (
            <button
              type="button"
              className="shortcut-button"
              aria-label="Test connection"
              title="Test connection"
              disabled={busy}
              onClick={(event) => {
                event.stopPropagation();
                void testConnection(row);
              }}
            >
              {busy ? 'Testing…' : 'Test'}
            </button>
          ) : null}
          <button
            type="button"
            className="list-workspace__icon-button is-danger"
            aria-label="Delete connection"
            title="Delete connection"
            disabled={busy}
            onClick={(event) => {
              event.stopPropagation();
              openConnectionDeleteDialog(row);
            }}
          >
            <TrashIcon size={16} />
          </button>
        </div>
      );
    },
    [
      cancelIntegrationSetup,
      cancellingSetupProviderKey,
      downloadTeamsAppPackage,
      downloadingTeamsPackageConnectionID,
      openConnectionDeleteDialog,
      providerByKey,
      rowActionBusy,
      startProviderBootstrap,
      startingBootstrapProviderKey,
      testConnection
    ]
  );

  const renderCloudTrailRowActions = useCallback(
    (row: CloudTrailSiteRow) => {
      const busy = cloudTrailActionBusy === row.siteId;
      const isEnabled = row.status === 'enabled';
      const isPending = row.status === 'action_required';
      return (
        <div className="cloud-integrations-row-actions cloud-integrations-row-actions--nowrap" onClick={(event) => event.stopPropagation()}>
          {isPending ? (
            <button
              type="button"
              className="cloud-integrations-row-actions__button"
              disabled={busy}
              onClick={() =>
                selectedCloudSignal === 'flowlogs'
                  ? void openFlowLogsResourcePicker(row)
                  : selectedCloudSignal === 'cloudwatch-logs'
                    ? openCloudWatchLogsPicker(row)
                    : void onboardCloudTrail(row)
              }
            >
              {busy ? 'Working…' : selectedCloudSignal === 'cloudwatch-logs' ? 'Create' : 'Open in AWS Console'}
            </button>
          ) : (
            <>
              <button
                type="button"
                className="cloud-integrations-row-actions__button"
                disabled={busy}
                onClick={() => (isEnabled ? void disableCloudTrail(row) : void enableCloudTrail(row))}
              >
                {busy ? 'Working…' : isEnabled ? 'Disable' : 'Enable'}
              </button>
              <button
                type="button"
                className="cloud-integrations-row-actions__button is-danger"
                disabled={busy}
                onClick={() => openCloudDeleteDialog(row)}
              >
                Delete
              </button>
            </>
          )}
        </div>
      );
    },
    [cloudTrailActionBusy, disableCloudTrail, enableCloudTrail, onboardCloudTrail, openCloudDeleteDialog, openCloudWatchLogsPicker, openFlowLogsResourcePicker, selectedCloudSignal]
  );

  const renderTargetRowActions = useCallback(
    (row: InteractionTarget) => {
      if (!row.id) {
        return null;
      }
      return (
        <button
          type="button"
          className="list-workspace__icon-button is-danger"
          aria-label="Delete target"
          title="Delete target"
          disabled={rowActionBusy === row.id}
          onClick={(event) => {
            event.stopPropagation();
            openTargetDeleteDialog(row);
          }}
        >
          <TrashIcon size={16} />
        </button>
      );
    },
    [openTargetDeleteDialog, rowActionBusy]
  );

  const renderRuleRowActions = useCallback(
    (row: InteractionRoutingRule) => {
      if (!row.id) {
        return null;
      }
      return (
        <button
          type="button"
          className="list-workspace__icon-button is-danger"
          aria-label="Delete rule"
          title="Delete rule"
          disabled={rowActionBusy === row.id}
          onClick={(event) => {
            event.stopPropagation();
            openRuleDeleteDialog(row);
          }}
        >
          <TrashIcon size={16} />
        </button>
      );
    },
    [openRuleDeleteDialog, rowActionBusy]
  );

  const connectionDrawerTabs = useMemo<TabItem[]>(() => {
    const items: TabItem[] = [{ id: 'overview', label: 'Connection' }];
    if (connectionDrawerMode === 'detail' && webhookFlow) {
      items.push({ id: 'webhook', label: 'Webhook' });
    }
    if (connectionDrawerMode === 'detail' && cmdbSyncFlow) {
      items.push({ id: 'cmdb', label: 'CMDB workflow' });
    }
    return items;
  }, [cmdbSyncFlow, connectionDrawerMode, webhookFlow]);

  if (!orgId) {
    return (
      <OrgModuleLayout
        title="Integrations"
        description="Connect ticketing systems and interaction channels for each organization."
        topSlot={<OrgModulePlaceholder intent="Select an organization to configure integrations." />}
      >
        <OrgModulePlaceholder intent="Integration settings are available once an organization is active." />
      </OrgModuleLayout>
    );
  }

  const currentConnectionProviderLabel = currentConnectionDefinition?.displayName ?? currentConnectionProvider;
  const cmdbAccessReadBadge = formatCMDBAccessStatus(cmdbAccessCheckResult?.readStatus);
  const cmdbAccessWriteBadge = formatCMDBAccessStatus(cmdbAccessCheckResult?.writeStatus);
  const cmdbAccessReadItems = (cmdbAccessCheckResult?.items ?? []).filter((item) => item.scope === 'read');
  const cmdbAccessWriteItems = (cmdbAccessCheckResult?.items ?? []).filter((item) => item.scope === 'write');
  const cmdbWritebackPolicyDirty =
    cmdbWritebackPolicy.updateExisting !== cmdbWritebackForm.updateExisting ||
    cmdbWritebackPolicy.createMissing !== cmdbWritebackForm.createMissing ||
    cmdbWritebackPolicy.mode !== cmdbWritebackForm.mode ||
    cmdbWritebackPolicy.publicationEnabled !== cmdbWritebackForm.publicationEnabled ||
    cmdbWritebackPolicy.allowedFields.join(',') !== cmdbWritebackForm.allowedFields.join(',');
  const cmdbWritePolicySummary =
    !cmdbAccessCheckResult
      ? 'Run the access check to validate CMDB read access and any policy-enabled writeback paths.'
      : !cmdbAccessCheckResult.policy.updateExisting && !cmdbAccessCheckResult.policy.createMissing
        ? 'CMDB writeback is currently disabled by policy, so write permission checks are skipped unless forced.'
        : `Current policy: update existing ${cmdbAccessCheckResult.policy.updateExisting ? 'on' : 'off'}, create missing ${cmdbAccessCheckResult.policy.createMissing ? 'on' : 'off'}.`;
  const renderCMDBAccessSummary = () => (
    <div className="org-shell__card integration-cmdb-card" style={{ marginBottom: '1rem' }}>
      <div className="integration-cmdb-card__header">
        <div>
          <h3 style={{ margin: 0 }}>CMDB Access Check</h3>
          <p style={{ margin: '0.35rem 0 0 0', color: 'var(--text-secondary)' }}>
            {`Validate ${currentConnectionProviderLabel} CMDB table access before sync or writeback.`}
          </p>
        </div>
        {connectionRecord?.id && (
          <button
            type="button"
            className="shortcut-button"
            onClick={() => void checkCMDBAccess(connectionRecord)}
            disabled={cmdbAccessCheckBusy || loadingCMDBWritebackPolicy || cmdbWritebackPolicyDirty || rowActionBusy === connectionRecord.id}
            title={cmdbWritebackPolicyDirty ? 'Save the policy before checking access.' : undefined}
          >
            {cmdbAccessCheckBusy ? 'Checking…' : 'Check CMDB Access'}
          </button>
        )}
      </div>
      {cmdbWritebackPolicyDirty && (
        <div className="ui-alert ui-alert--warning" style={{ marginBottom: '1rem' }}>
          Save the operating policy before checking access so REAP validates the correct write permissions.
        </div>
      )}
      {!cmdbAccessCheckResult ? (
        <p style={{ margin: 0, color: 'var(--text-secondary)' }}>Not checked for the saved policy yet.</p>
      ) : (
        <>
          <div className="integration-cmdb-stats">
            <div className="integration-cmdb-stat">
              <span className="integration-cmdb-stat__label">Last checked</span>
              <strong className="integration-cmdb-stat__value">{formatDateTime(cmdbAccessCheckResult.checkedAt)}</strong>
            </div>
            <div className="integration-cmdb-stat">
              <div className="integration-cmdb-stat__headline">
                <span className="integration-cmdb-stat__label">Read access</span>
                <span className={cmdbAccessReadBadge.className}>{cmdbAccessReadBadge.label}</span>
              </div>
              <strong className="integration-cmdb-stat__value">{`${cmdbAccessCheckResult.readPassedCount}/${cmdbAccessCheckResult.readRequiredCount} checks`}</strong>
            </div>
            <div className="integration-cmdb-stat">
              <div className="integration-cmdb-stat__headline">
                <span className="integration-cmdb-stat__label">Write access</span>
                <span className={cmdbAccessWriteBadge.className}>{cmdbAccessWriteBadge.label}</span>
              </div>
              <strong className="integration-cmdb-stat__value">{`${cmdbAccessCheckResult.writePassedCount}/${cmdbAccessCheckResult.writeRequiredCount} checks`}</strong>
            </div>
            <div className="integration-cmdb-stat">
              <span className="integration-cmdb-stat__label">Issues</span>
              <strong className="integration-cmdb-stat__value">{String(cmdbAccessCheckResult.issueCount)}</strong>
            </div>
          </div>
          <p className="integration-cmdb-note">{cmdbWritePolicySummary}</p>
          <details style={{ marginTop: '0.75rem' }}>
            <summary style={{ cursor: 'pointer', fontWeight: 600 }}>Per-table checks</summary>
            <div style={{ display: 'grid', gap: '0.75rem', marginTop: '0.75rem' }}>
              {[
                { title: 'Read checks', items: cmdbAccessReadItems },
                { title: 'Write checks', items: cmdbAccessWriteItems }
              ].map((group) => (
                <div key={group.title} style={{ display: 'grid', gap: '0.5rem' }}>
                  <strong>{group.title}</strong>
                  {group.items.length === 0 ? (
                    <p style={{ margin: 0, color: 'var(--text-secondary)' }}>No checks recorded.</p>
                  ) : (
                    group.items.map((item) => {
                      const badge = formatCMDBAccessStatus(item.status);
                      return (
                        <div
                          key={`${group.title}-${item.scope}-${item.table}-${item.operation}`}
                          style={{ border: '1px solid var(--border-subtle)', borderRadius: '0.75rem', padding: '0.75rem' }}
                        >
                          <div style={{ alignItems: 'center', display: 'flex', gap: '0.75rem', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                            <div style={{ fontWeight: 600 }}>
                              {item.table} · {item.operation}
                            </div>
	                            <span className={badge.className}>{item.enabled ? badge.label : 'Skipped'}</span>
                          </div>
                          <div style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginTop: '0.35rem' }}>
                            {item.message ?? item.impact ?? (item.enabled ? 'No detail returned.' : 'Skipped because current writeback policy does not require this check.')}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              ))}
            </div>
          </details>
        </>
      )}
    </div>
  );
  const cmdbReconciliationPolicyDirty =
    JSON.stringify(policyToCMDBReconciliationForm(cmdbReconciliationPolicy)) !== JSON.stringify(cmdbReconciliationForm);
  const showConnectionOverview = connectionDrawerMode === 'create' || connectionDrawerTab === 'overview';
  const showConnectionCMDB = connectionDrawerMode === 'detail' && cmdbSyncFlow && connectionDrawerTab === 'cmdb';
  const showConnectionWebhook = connectionDrawerMode === 'detail' && webhookFlow && connectionDrawerTab === 'webhook';

  const filterBar = (
    <div className="list-workspace__filter-grid">
      <label className="form-field">
        <span className="form-field__label">Provider</span>
        <select className="select" value={filters.provider} onChange={(event) => updateFilters({ provider: event.target.value as FilterProvider })}>
          <option value="all">All providers</option>
          {providerDefinitions.map((provider) => (
            <option key={provider.providerKey} value={provider.providerKey}>
              {provider.displayName}
            </option>
          ))}
        </select>
      </label>
      <label className="form-field">
        <span className="form-field__label">Status</span>
        <select className="select" value={filters.status} onChange={(event) => updateFilters({ status: event.target.value as FilterState['status'] })}>
          <option value="all">All statuses</option>
          <option value="active">Active</option>
          <option value="pending">Pending</option>
          <option value="error">Error</option>
          <option value="disabled">Disabled</option>
        </select>
      </label>
      <label className="form-field">
        <span className="form-field__label">Search</span>
        <input className="input" placeholder="Search connections…" value={filters.q} onChange={(event) => updateFilters({ q: event.target.value })} />
      </label>
    </div>
  );

  const cloudIntegrationSelector = (
    <div className="cloud-integrations-filter">
      <div className="cloud-integrations-filter__header">
        <h2 className="cloud-integrations-filter__title">Cloud integrations</h2>
      </div>
      <div className="cloud-integrations-filter__inputs org-settings-form">
        <label className="form-field">
          <span className="form-field__label">Cloud provider</span>
          <select
            className="select"
            value={selectedCloudProvider}
            onChange={(event) => {
              const nextProvider = event.target.value as CloudProvider | '';
              setSelectedCloudProvider(nextProvider);
              setSelectedCloudSignal('');
            }}
          >
            <option value="">Select provider</option>
            {cloudProviderOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        <label className="form-field">
          <span className="form-field__label">Integration source</span>
          <select
            className="select"
            value={selectedCloudSignal}
            disabled={!selectedCloudProvider}
            onChange={(event) => setSelectedCloudSignal(event.target.value as CloudSignal | '')}
          >
            <option value="">{selectedCloudProvider ? 'Select source' : 'Select provider first'}</option>
            {selectedCloudSignalOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
      </div>
      {cloudSelectionReady ? (
        <p className="cloud-integrations-filter__selection">
          Current selection: {selectedCloudProviderOption?.label} / {selectedCloudSignalOption?.label}
        </p>
      ) : (
        <div className="cloud-integrations-filter__empty">
          <strong className="cloud-integrations-filter__empty-title">Select a cloud provider and source to start.</strong>
          <p className="cloud-integrations-filter__empty-body">
            Cloud onboarding appears here after you pick the relevant provider and source.
          </p>
        </div>
      )}
    </div>
  );

  const connectionEmptyState =
    !loadingConnections && paginationMeta.totalCount === 0 && !filterActive ? (
      <div className="empty-hero">
        <h3 className="empty-hero__title">Connect your first integration</h3>
        <p className="empty-hero__body">Add ServiceNow, Jira, Slack, or Teams. Ticketing stays system-of-record aware, and channel installs stay org-scoped.</p>
        <div className="chip-row">
          <button type="button" className="chip-button" onClick={openCreateConnectionDrawer}>
            Create integration connection
          </button>
        </div>
      </div>
    ) : filterActive ? (
      <p>No connections match the current filters.</p>
    ) : (
      <p>No integrations configured yet.</p>
    );

  const renderCloudTrailEmptyState = () => {
    if (cloudTrailLoading) {
      return <p>Loading AWS cloud sites…</p>;
    }
    if (cloudTrailItems.length === 0) {
      return (
        <div className="empty-hero">
          <h3 className="empty-hero__title">No AWS cloud sites available</h3>
          <p className="empty-hero__body">
            Create AWS cloud sites first. {selectedCloudSignalLabel} onboarding is limited to those site records.
          </p>
        </div>
      );
    }
    return null;
  };

  const cloudTrailDrawerBusy = cloudTrailDrawerRecord ? cloudTrailActionBusy === cloudTrailDrawerRecord.siteId : false;

  const cloudTrailDrawerContent = (
    <div className="org-settings-form">
      <label>
        <span>Status</span>
        <input
          type="text"
          value={
            cloudTrailDrawerRecord?.status === 'action_required'
              ? 'Action required'
              : cloudTrailDrawerRecord?.status ?? 'Action required'
          }
          readOnly
        />
      </label>
      <label>
        <span>Site</span>
        <input type="text" value={cloudTrailDrawerRecord?.siteName ?? '—'} readOnly />
      </label>
      <label>
        <span>ID / Region</span>
        <input type="text" value={cloudTrailDrawerRecord ? `${cloudTrailDrawerRecord.awsAccountId} / ${cloudTrailDrawerRecord.region}` : '—'} readOnly />
      </label>
      {selectedCloudSignal === 'flowlogs' ? (
        <label>
          <span>Resources</span>
          <input
            type="text"
            value={
              cloudTrailDrawerRecord
                ? `${cloudTrailDrawerRecord.vpcIds.length} VPC / ${cloudTrailDrawerRecord.transitGatewayIds.length} TGW`
                : '—'
            }
            readOnly
          />
        </label>
      ) : null}
      {selectedCloudSignal === 'cloudwatch-logs' ? (
        <>
          <label>
            <span>Log groups</span>
            <input
              type="text"
              value={cloudTrailDrawerRecord ? `${cloudTrailDrawerRecord.logGroups.length} selected` : '—'}
              readOnly
            />
          </label>
          {cloudWatchLogsAPIKey ? (
            <label>
              <span>API key</span>
              <textarea value={cloudWatchLogsAPIKey} readOnly rows={3} />
            </label>
          ) : null}
        </>
      ) : null}
      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
        {cloudTrailDrawerRecord?.status === 'action_required' ? (
          <button
            type="button"
            className="shortcut-button"
            disabled={cloudTrailDrawerBusy}
            onClick={() =>
              cloudTrailDrawerRecord &&
              void (selectedCloudSignal === 'flowlogs'
                ? openFlowLogsResourcePicker(cloudTrailDrawerRecord)
                : selectedCloudSignal === 'cloudwatch-logs'
                  ? openCloudWatchLogsPicker(cloudTrailDrawerRecord)
                : onboardCloudTrail(cloudTrailDrawerRecord))
            }
          >
            {cloudTrailDrawerBusy ? 'Working…' : selectedCloudSignal === 'cloudwatch-logs' ? 'Create' : 'Open in AWS Console'}
          </button>
        ) : (
          <>
            {awsQuickCreateURL ? (
              <button
                type="button"
                className="shortcut-button"
                onClick={() => {
                  openBackendQuickCreateURL(null, awsQuickCreateURL);
                }}
              >
                Open in AWS Console
              </button>
            ) : null}
            {cloudTrailDrawerRecord?.status === 'enabled' ? (
              <button
                type="button"
                className="shortcut-button"
                disabled={cloudTrailDrawerBusy}
                onClick={() => cloudTrailDrawerRecord && void disableCloudTrail(cloudTrailDrawerRecord)}
              >
                {cloudTrailDrawerBusy ? 'Working…' : 'Disable'}
              </button>
            ) : (
              <button
                type="button"
                className="shortcut-button"
                disabled={cloudTrailDrawerBusy}
                onClick={() => cloudTrailDrawerRecord && void enableCloudTrail(cloudTrailDrawerRecord)}
              >
                {cloudTrailDrawerBusy ? 'Working…' : 'Enable'}
              </button>
            )}
            <button
              type="button"
              className="shortcut-button"
              disabled={cloudTrailDrawerBusy}
              onClick={() => cloudTrailDrawerRecord && void deleteCloudTrail(cloudTrailDrawerRecord)}
            >
              {cloudTrailDrawerBusy ? 'Working…' : 'Delete'}
            </button>
          </>
        )}
      </div>
    </div>
  );

  const renderFlowLogsResourceRows = (kind: 'vpc' | 'tgw', items: AWSFlowLogsResourceOption[], selectedIds: string[]) => {
    if (items.length === 0) {
      return <p style={{ color: 'var(--text-secondary)', margin: 0 }}>No eligible resources found.</p>;
    }
    return (
      <div style={{ display: 'grid', gap: '0.5rem' }}>
        {items.map((item) => (
          <label
            key={item.id}
            style={{
              alignItems: 'center',
              border: '1px solid var(--border-subtle)',
              borderRadius: 8,
              display: 'grid',
              gap: '0.75rem',
              gridTemplateColumns: 'auto minmax(0, 1fr)',
              padding: '0.75rem'
            }}
          >
            <input
              type="checkbox"
              checked={selectedIds.includes(item.id)}
              onChange={() => toggleSelectedFlowLogResource(kind, item.id)}
            />
            <span style={{ minWidth: 0 }}>
              <strong>{item.name}</strong>
              <span style={{ color: 'var(--text-secondary)', display: 'block', fontSize: '0.85rem' }}>
                {[item.id, item.cidr, item.state].filter(Boolean).join(' / ')}
              </span>
            </span>
          </label>
        ))}
      </div>
    );
  };

  const flowLogsSelectedCount = selectedFlowLogVPCIDs.length + selectedFlowLogTGWIDs.length;
  const flowLogsPickerContent = (
    <div className="org-settings-form">
      {flowLogsResourceLoading ? (
        <p style={{ color: 'var(--text-secondary)', margin: 0 }}>Loading resources…</p>
      ) : flowLogsResourceOptions ? (
        <>
          <label>
            <span>Site</span>
            <input type="text" value={`${flowLogsResourceOptions.awsAccountId} / ${flowLogsResourceOptions.region}`} readOnly />
          </label>
          <section style={{ display: 'grid', gap: '0.75rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem' }}>
              <h3 style={{ fontSize: '1rem', margin: 0 }}>VPCs</h3>
              <button
                type="button"
                className="shortcut-button"
                onClick={() => setSelectedFlowLogVPCIDs(flowLogsResourceOptions.vpcs.map((item) => item.id).filter(Boolean))}
              >
                Select all
              </button>
            </div>
            {renderFlowLogsResourceRows('vpc', flowLogsResourceOptions.vpcs, selectedFlowLogVPCIDs)}
          </section>
          <section style={{ display: 'grid', gap: '0.75rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem' }}>
              <h3 style={{ fontSize: '1rem', margin: 0 }}>Transit gateways</h3>
              <button
                type="button"
                className="shortcut-button"
                onClick={() => setSelectedFlowLogTGWIDs(flowLogsResourceOptions.transitGateways.map((item) => item.id).filter(Boolean))}
              >
                Select all
              </button>
            </div>
            {renderFlowLogsResourceRows('tgw', flowLogsResourceOptions.transitGateways, selectedFlowLogTGWIDs)}
          </section>
        </>
      ) : (
        <p style={{ color: 'var(--text-secondary)', margin: 0 }}>No resources loaded.</p>
      )}
    </div>
  );

  const cloudWatchLogsPickerContent = (
    <div className="org-settings-form">
      <label>
        <span>Site</span>
        <input
          type="text"
          value={cloudWatchLogsPickerRow ? `${cloudWatchLogsPickerRow.awsAccountId} / ${cloudWatchLogsPickerRow.region}` : '—'}
          readOnly
        />
      </label>
      <label>
        <span>Log groups</span>
        <textarea
          value={cloudWatchLogsInput}
          onChange={(event) => setCloudWatchLogsInput(event.target.value)}
          rows={8}
          placeholder="/aws/lambda/example"
        />
      </label>
    </div>
  );

  return (
    <OrgModuleLayout title="Integrations" description="Ticketing systems, channel installs, interaction targets, and routing rules.">
      {cloudTrailErrorMessage && (
        <p className="org-shell__banner" role="alert">
          {cloudTrailErrorMessage}
        </p>
      )}
      {azureResourceChangesSecret && (
        <div className="org-shell__banner" role="status">
          <strong>Azure resource changes are ready for {azureResourceChangesSecret.subscriptionId}.</strong>
          <p>Copy this one-time secret into the Azure Portal ARM secureString parameter. REAP will not show it again.</p>
          <CopyField value={azureResourceChangesSecret.secret} label="Copy Event Grid API key" />
          <a href={azureResourceChangesSecret.launchUrl} target="_blank" rel="noreferrer">Open Azure setup</a>
          <button type="button" onClick={() => setAzureResourceChangesSecret(null)}>Dismiss</button>
        </div>
      )}
      {errorMessage && (
        <p className="org-shell__banner" role="alert">
          {errorMessage}
        </p>
      )}
      {integrationStatusMessage && (
        <p className="org-shell__banner" role="status">
          {integrationStatusMessage}
        </p>
      )}
      <section className="cloud-integrations-panel">
        {cloudIntegrationSelector}
        {selectedCloudProvider === 'azure' && selectedCloudSignal === 'resource-changes' ? (
          <div className="cloud-integrations-panel__content"><div className="cloud-integrations-list"><ListWorkspace headerLabel="Azure resource changes" rows={azureResourceChangeRows} rowId={(row) => row.subscriptionId} columns={azureResourceChangesColumns} paginationMeta={{ totalCount: azureResourceChangeRows.length, pageCount: 1, hasPrevPage: false, hasNextPage: false, exactTotal: azureResourceChangeRows.length }} emptyState={<p>No Azure subscriptions available for resource-change logging.</p>} loading={azureResourceChangesLoading} onRefresh={fetchAzureResourceChanges} rowActions={renderAzureResourceChangesActions} selectionDisabled /></div></div>
        ) : cloudSignalSupported ? (
          <div className="cloud-integrations-panel__content">
            <div className="cloud-integrations-list">
              <ListWorkspace
                headerLabel={selectedCloudSignalLabel}
                headerStatus={cloudTrailHeaderStatus}
                rows={cloudTrailItems}
                rowId={(row) => row.siteId}
                columns={cloudTrailColumns}
                paginationMeta={cloudTrailPaginationMeta}
                emptyState={renderCloudTrailEmptyState()}
                loading={cloudTrailLoading}
                onRowClick={openCloudTrailDetailDrawer}
                onRefresh={fetchCloudTrailIntegrations}
                rowActions={renderCloudTrailRowActions}
                selectionDisabled
              />
            </div>
          </div>
        ) : null}
      </section>

      {pendingSetupRows.length > 0 ? (
        <div style={{ marginBottom: '1.5rem' }}>
          <ListWorkspace
            headerLabel="Setup requests"
            rows={pendingSetupRows}
            rowId={(row, index) => row.id ?? `${index}`}
            columns={connectionColumns}
            hidePagination
            onRowClick={openConnectionDrawer}
            onRefresh={refreshIntegrationSetups}
            rowActions={renderConnectionRowActions}
            selectionDisabled
          />
        </div>
      ) : null}

      <ListWorkspace
        headerLabel="Connections"
        rows={connections}
        rowId={(row, index) => row.id ?? `${index}`}
        columns={connectionColumns}
        page={page}
        pageSize={pageSize}
        paginationMeta={paginationMeta}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        emptyState={connectionEmptyState}
        loading={loadingConnections}
        highlightRowId={highlightRowId}
        onRowClick={openConnectionDrawer}
        onAdd={openCreateConnectionDrawer}
        onRefresh={() => {
          void fetchConnections();
          void refreshIntegrationSetups();
        }}
        toolbarActions={
          bootstrapProviderDefinitions.length > 0 ? (
            <>
              {bootstrapProviderDefinitions.map((definition) => {
                const connecting = startingBootstrapProviderKey === definition.providerKey;
                return (
                  <button
                    key={definition.providerKey}
                    type="button"
                    className="ui-button"
                    disabled={Boolean(startingBootstrapProviderKey) || !orgId}
                    onClick={() => void startProviderBootstrap(definition)}
                  >
                    {connecting ? `Connecting ${definition.displayName}…` : `Connect ${definition.displayName}`}
                  </button>
                );
              })}
            </>
          ) : undefined
        }
        filterBar={filterBar}
        onFilterReset={resetFilters}
        filterActive={filterActive}
        rowActions={renderConnectionRowActions}
      />

      <div style={{ marginTop: '1.5rem' }}>
        <ListWorkspace
          headerLabel="Interaction Targets"
          rows={targets}
          rowId={(row, index) => row.id ?? `${index}`}
          columns={targetColumns}
          emptyState={<p>No interaction targets configured yet.</p>}
          loading={loadingTargets}
          onRowClick={(row) => {
            setTargetDrawerMode('detail');
            setTargetRecord(row);
            setTargetForm(targetFormFromRecord(row, defaultInteractionProvider));
            setTargetDrawerOpen(true);
          }}
          onAdd={() => {
            setTargetDrawerMode('create');
            setTargetRecord(null);
            setTargetForm(defaultTargetForm(defaultInteractionProvider));
            setTargetDrawerOpen(true);
          }}
          onRefresh={fetchTargets}
          rowActions={renderTargetRowActions}
        />
      </div>

      <div style={{ marginTop: '1.5rem' }}>
        <ListWorkspace
          headerLabel="Routing Rules"
          rows={routingRules}
          rowId={(row, index) => row.id ?? `${index}`}
          columns={ruleColumns}
          emptyState={<p>No routing rules configured yet.</p>}
          loading={loadingRules}
          onRowClick={(row) => {
            setRuleDrawerMode('detail');
            setRuleRecord(row);
            setRuleForm(ruleFormFromRecord(row, defaultInteractionProvider));
            setRuleDrawerOpen(true);
          }}
          onAdd={() => {
            setRuleDrawerMode('create');
            setRuleRecord(null);
            setRuleForm(defaultRuleForm(defaultInteractionProvider));
            setRuleDrawerOpen(true);
          }}
          onRefresh={fetchRoutingRules}
          rowActions={renderRuleRowActions}
        />
      </div>

      <Drawer
        open={cloudTrailDrawerOpen}
        title={cloudTrailDrawerRecord?.siteName ? `${selectedCloudSignalLabel} · ${cloudTrailDrawerRecord.siteName}` : `${selectedCloudSignalLabel} detail`}
        subtitle={cloudTrailDrawerRecord ? `${cloudTrailDrawerRecord.awsAccountId} / ${cloudTrailDrawerRecord.region}` : undefined}
        onClose={closeCloudTrailDrawer}
        width="wide"
      >
        {cloudTrailDrawerContent}
      </Drawer>

      <Drawer
        open={flowLogsPickerOpen}
        title={flowLogsPickerRow?.siteName ? `Select Flow Logs resources · ${flowLogsPickerRow.siteName}` : 'Select Flow Logs resources'}
        subtitle={flowLogsPickerRow ? `${flowLogsPickerRow.awsAccountId} / ${flowLogsPickerRow.region}` : undefined}
        onClose={closeFlowLogsResourcePicker}
        width="wide"
        footer={
          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
            <button type="button" className="shortcut-button" onClick={closeFlowLogsResourcePicker}>
              Cancel
            </button>
            <button
              type="button"
              className="shortcut-button"
              disabled={flowLogsResourceLoading || cloudTrailActionBusy === flowLogsPickerRow?.siteId || flowLogsSelectedCount === 0}
              onClick={() => void confirmFlowLogsResourceSelection()}
            >
              {cloudTrailActionBusy === flowLogsPickerRow?.siteId ? 'Working…' : `Open in AWS Console (${flowLogsSelectedCount})`}
            </button>
          </div>
        }
      >
        {flowLogsPickerContent}
      </Drawer>

      <Drawer
        open={cloudWatchLogsPickerOpen}
        title={cloudWatchLogsPickerRow?.siteName ? `Configure CloudWatch Logs · ${cloudWatchLogsPickerRow.siteName}` : 'Configure CloudWatch Logs'}
        subtitle={cloudWatchLogsPickerRow ? `${cloudWatchLogsPickerRow.awsAccountId} / ${cloudWatchLogsPickerRow.region}` : undefined}
        onClose={closeCloudWatchLogsPicker}
        width="wide"
        footer={
          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
            <button type="button" className="shortcut-button" onClick={closeCloudWatchLogsPicker}>
              Cancel
            </button>
            <button
              type="button"
              className="shortcut-button"
              disabled={cloudTrailActionBusy === cloudWatchLogsPickerRow?.siteId}
              onClick={() => void confirmCloudWatchLogsSelection()}
            >
              {cloudTrailActionBusy === cloudWatchLogsPickerRow?.siteId ? 'Working…' : 'Create'}
            </button>
          </div>
        }
      >
        {cloudWatchLogsPickerContent}
      </Drawer>

      <Drawer
        open={connectionDrawerOpen}
        title={connectionDrawerMode === 'create' ? 'New integration' : connectionRecord?.name ?? 'Integration detail'}
        subtitle={providerDescription(currentConnectionDefinition)}
        onClose={closeConnectionDrawer}
        width="wide"
      >
        <form
          className="org-settings-form"
          onSubmit={(event) => {
            event.preventDefault();
            void submitConnection();
          }}
        >
          {connectionDrawerTabs.length > 1 && (
            <Tabs items={connectionDrawerTabs} activeId={connectionDrawerTab} onChange={(id) => setConnectionDrawerTab(id as ConnectionDrawerTab)} />
          )}

          {showConnectionCMDB && (
            <nav className="integration-cmdb-workflow-tabs" aria-label="CMDB workflow">
              {([
                ['access', '1. Policy & Access'],
                ['sync', '2. Sync & Inspect'],
                ['matching', `3. Match Review${cmdbDeviceCrosswalk?.summary.reviewDecisionCount ? ` (${cmdbDeviceCrosswalk.summary.reviewDecisionCount})` : ''}`],
                ['interpretation', '4. Interpretation'],
                ['governance', '5. Governance']
              ] as Array<[CMDBWorkspaceTab, string]>).map(([id, label]) => {
                const disabled = id === 'matching' && !cmdbSourceEvidence?.identityCount;
                return (
                  <button
                    key={id}
                    type="button"
                    aria-current={cmdbWorkspaceTab === id ? 'step' : undefined}
                    disabled={disabled}
                    title={disabled ? 'Complete the first CMDB sync before continuing.' : undefined}
                    onClick={() => setCMDBWorkspaceTab(id)}
                  >
                    {label}
                  </button>
                );
              })}
            </nav>
          )}

          {showConnectionOverview && (
            <div className="org-shell__card" style={{ padding: '1rem', marginBottom: '1rem' }}>
              <label>
                <span>Provider</span>
                <select value={connectionForm.provider} disabled={connectionDrawerMode === 'detail'} onChange={(event) => updateConnectionProvider(event.target.value)}>
                  {providerDefinitions.map((provider) => (
                    <option key={provider.providerKey} value={provider.providerKey}>
                      {provider.displayName}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <span>Connection name</span>
                <input type="text" value={connectionForm.name} onChange={updateConnectionName} placeholder={currentConnectionDefinition?.displayName ?? currentConnectionProvider} />
              </label>
              {currentConnectionFields.map((field) => {
                const value = connectionForm.values[field.fieldKey] ?? '';
                const placeholder =
                  field.secret && connectionDrawerMode === 'detail'
                    ? `Leave blank to keep current ${field.label.toLowerCase()}`
                    : field.placeholder ?? '';
                const options = Array.isArray(field.attributes?.options) ? (field.attributes?.options as Array<{ value: string; label: string }>) : [];
                if (field.inputType === 'select') {
                  return (
                    <label key={field.fieldKey}>
                      <span>{field.label}</span>
                      <select value={value} onChange={updateConnectionValue(field.fieldKey)}>
                        <option value="">Select {field.label}</option>
                        {options.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      {field.description ? <small style={{ color: 'var(--text-secondary)' }}>{field.description}</small> : null}
                    </label>
                  );
                }
                return (
                  <label key={field.fieldKey}>
                    <span>{field.label}</span>
                    <input
                      type={field.secret ? 'password' : field.inputType === 'url' ? 'url' : 'text'}
                      value={value}
                      onChange={updateConnectionValue(field.fieldKey)}
                      placeholder={placeholder}
                    />
                    {field.description ? <small style={{ color: 'var(--text-secondary)' }}>{field.description}</small> : null}
                  </label>
                );
              })}

              {connectionRecord?.status && (
                <label>
                  <span>Status</span>
                  <input type="text" value={connectionRecord.status} readOnly />
                </label>
              )}
              {connectionRecord?.lastError && (
                <div className="org-shell__banner" role="alert">
                  {connectionRecord.lastError}
                </div>
              )}
              <div style={{ marginTop: '1rem', display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                {connectionDrawerMode === 'detail' && connectionRecord?.id && supportsConnectionTest(currentConnectionDefinition) && (
                  <button
                    type="button"
                    className="shortcut-button"
                    onClick={() => void testConnection(connectionRecord)}
                    disabled={rowActionBusy === connectionRecord.id}
                  >
                    {rowActionBusy === connectionRecord.id ? 'Testing…' : 'Test Connection'}
                  </button>
                )}
                <button type="submit" className="shortcut-button" disabled={savingConnection}>
                  {savingConnection ? 'Saving…' : connectionDrawerMode === 'create' ? 'Create Connection' : 'Save Changes'}
                </button>
              </div>
            </div>
          )}

          {showConnectionCMDB && (
            <>
              {cmdbWorkspaceTab === 'access' && <div role="status" aria-live="polite" className={`integration-cmdb-preflight ${connectionTestFeedback?.tone === 'error' ? 'needs-attention' : connectionRecord?.status?.toLowerCase() === 'active' ? 'is-ready' : 'needs-attention'}`}>
                <div>
                  <strong>{connectionTestFeedback?.message ?? (connectionRecord?.status?.toLowerCase() === 'active' ? 'Provider connection is active' : 'Provider connection requires attention')}</strong>
                  <span>{connectionTestFeedback ? 'Connection test completed. This result will clear automatically.' : connectionRecord?.status?.toLowerCase() === 'active' ? 'Continue with the CMDB-specific table access check below.' : 'Test and save the provider connection before reading CMDB data.'}</span>
                </div>
                {connectionRecord?.id && supportsConnectionTest(currentConnectionDefinition) && <button type="button" className="shortcut-button" disabled={rowActionBusy === connectionRecord.id} onClick={() => void testConnection(connectionRecord)}>{rowActionBusy === connectionRecord.id ? 'Testing…' : 'Test connection'}</button>}
              </div>}
              {cmdbWorkspaceTab === 'sync' && (
                <div className="ui-alert ui-alert--info" style={{ marginBottom: '1rem' }}>
                  <strong>{cmdbWritebackPolicy.executionEnabled ? 'Synchronization remains read-only.' : 'Safe default: report only.'}</strong>{' '}
                  This step imports provider evidence for comparison. It does not create or update CMDB records.
                </div>
              )}
              {cmdbWorkspaceTab === 'sync' && connectionRecord?.id && (
                <CMDBSetupGuide
                  sourceIdentityCount={cmdbSourceEvidence?.identityCount ?? 0}
                  ciCount={cmdbSourceEvidence?.countsByDomain.cis ?? 0}
                  reviewCount={cmdbDeviceCrosswalk?.summary.reviewDecisionCount ?? 0}
                  reviewRecordCount={cmdbDeviceCrosswalk?.summary.reviewCIRecordCount ?? 0}
                  duplicateWarningCount={cmdbDeviceCrosswalk?.summary.duplicateGroupCount ?? 0}
                  writebackEnabled={cmdbWritebackPolicy.executionEnabled}
                  syncAllowed={connectionRecord.status?.toLowerCase() === 'active'}
                  syncing={cmdbSyncBusy}
                  syncState={cmdbSyncJob}
                  onStartSync={() => void startCMDBSync(connectionRecord.id!)}
                  onReview={() => {
                    setCMDBCrosswalkFilter('needs_review');
                    setCMDBWorkspaceTab('matching');
                  }}
                />
              )}
              {cmdbWorkspaceTab === 'matching' && connectionRecord?.id && (
                <CMDBDeviceCrosswalk
                  data={cmdbDeviceCrosswalk}
                  loading={loadingCMDBDeviceCrosswalk}
                  filter={cmdbCrosswalkFilter}
                  onFilterChange={setCMDBCrosswalkFilter}
                  onRefresh={() => void fetchCMDBDeviceCrosswalk(connectionRecord.id!)}
                  onReview={reviewCMDBIdentity}
                  canCreateCI={cmdbWritebackPolicy.executionEnabled}
                  onCreateCI={createMissingCMDBCI}
                />
              )}
              <div hidden={cmdbWorkspaceTab !== 'sync'} className="org-shell__card integration-cmdb-card" style={{ marginBottom: '1rem' }}>
                <div className="integration-cmdb-card__header">
                  <div>
                    <h3 style={{ margin: 0 }}>Imported CMDB snapshot</h3>
                    <p style={{ margin: '0.35rem 0 0 0', color: 'var(--text-secondary)' }}>
                      What the CMDB returned during the latest read-only sync. This is audit information; there is nothing to configure here.
                    </p>
                  </div>
                </div>
                <div className="integration-cmdb-stats" style={{ marginTop: '0.75rem' }}>
                  {Object.entries(cmdbSourceEvidence?.countsByDomain ?? {}).sort(([left], [right]) => left.localeCompare(right)).map(([domain, count]) => <div className="integration-cmdb-stat" key={domain}>
                    <span className="integration-cmdb-stat__label">{CMDB_SOURCE_DOMAIN_LABELS[domain] ?? domain.replaceAll('_', ' ')}</span>
                    <strong className="integration-cmdb-stat__value">{count}</strong>
                  </div>)}
                  <div className="integration-cmdb-stat">
                    <span className="integration-cmdb-stat__label">All imported items</span>
                    <strong className="integration-cmdb-stat__value">{cmdbSourceEvidence?.identityCount ?? 0}</strong>
                  </div>
                </div>
                <details className="integration-cmdb-disclosure">
                  <summary>Technical sync history and retention</summary>
                  <div className="integration-cmdb-disclosure__body">
                <div className="integration-cmdb-meta" style={{ marginTop: '0.75rem' }}>
                  <div className="integration-cmdb-meta__item"><span>Provider deleted</span><strong>{cmdbSourceEvidence?.providerDeletedCount ?? 0}</strong></div>
                  <div className="integration-cmdb-meta__item"><span>Provider retired</span><strong>{cmdbSourceEvidence?.providerRetiredCount ?? 0}</strong></div>
                  <div className="integration-cmdb-meta__item"><span>Out of scope</span><strong>{cmdbSourceEvidence?.outOfScopeCount ?? 0}</strong></div>
                  <div className="integration-cmdb-meta__item"><span>Unknown visibility</span><strong>{cmdbSourceEvidence?.unknownCount ?? 0}</strong></div>
                </div>
                <p className="integration-cmdb-note" style={{ marginTop: '0.75rem' }}>
                  Active generation: {cmdbSourceEvidence?.activeGenerationId ?? 'Mixed domain generations or no completed synchronization'}
                </p>
                <h4 style={{ margin: '1rem 0 0.5rem' }}>Recent source identities</h4>
                <div style={{ overflowX: 'auto' }}>
                  <table className="integration-cmdb-table">
                    <thead><tr><th>Domain</th><th>Native type</th><th>External ID</th><th>Lifecycle</th><th>Observed</th><th /></tr></thead>
                    <tbody>{cmdbSourceRecords.map((record) => <tr key={record.identityId}><td>{record.domain}</td><td>{record.nativeType}</td><td><code>{record.externalId}</code></td><td>{record.latestOccurrence?.lifecycleObservation?.replaceAll('_', ' ') ?? 'history available'}</td><td>{formatDateTime(record.activeVersion?.observedAt ?? null)}</td><td>{connectionRecord?.id && <button type="button" className="shortcut-button" onClick={() => void fetchCMDBSourceRecordHistory(connectionRecord.id!, record.identityId)}>History</button>}</td></tr>)}</tbody>
                  </table>
                  {cmdbSourceRecords.length === 0 && <p className="integration-cmdb-note">No source identities have been synchronized.</p>}
                </div>
                {cmdbSourceRecordHistory && <div style={{ marginTop: '0.75rem', border: '1px solid var(--border-subtle)', borderRadius: '0.75rem', padding: '0.75rem' }}>
                  <div className="integration-cmdb-card__header"><strong>{cmdbSourceRecordHistory.record.externalId}</strong><button type="button" className="shortcut-button" onClick={() => { setCMDBSourceRecordHistory(null); setCMDBSourceIdentityHistory(null); }}>Close</button></div>
                  <p className="integration-cmdb-note">{cmdbSourceRecordHistory.versions.length} immutable versions · {cmdbSourceRecordHistory.occurrences.length} occurrences · {cmdbSourceIdentityHistory?.decisions.length ?? 0} decisions · {cmdbSourceIdentityHistory?.mappings.length ?? 0} mappings</p>
                  <div className="integration-cmdb-meta">{cmdbSourceRecordHistory.occurrences.slice(0, 6).map((occurrence) => <div className="integration-cmdb-meta__item" key={occurrence.id}><span>{occurrence.lifecycleObservation.replaceAll('_', ' ')}</span><strong>{formatDateTime(occurrence.observedAt)}</strong></div>)}</div>
                </div>}
                {cmdbSourceRetention && connectionRecord?.id && <div className="integration-cmdb-section" style={{ marginTop: '1rem' }}>
                  <p className="integration-cmdb-section__eyebrow">Section 3 · Retention</p>
                  <h4 className="integration-cmdb-section__title">Evidence retention</h4>
                  <p className="integration-cmdb-note">Versioned per connection. Active and mapping-referenced evidence is retained regardless of age.</p>
                  <div className="integration-cmdb-policy-grid">
                    {([['Source versions', 'sourceVersionDays', 30], ['Occurrences', 'occurrenceDays', 30], ['Identity decisions', 'identityDecisionDays', 365], ['Resolved findings', 'findingDays', 365]] as const).map(([label, key, min]) => <label key={key}><span>{label} (days)</span><input type="number" min={min} value={cmdbSourceRetention[key]} onChange={(event) => setCMDBSourceRetention((current) => current ? { ...current, [key]: Number(event.target.value) } : current)} /></label>)}
                  </div>
                  <div className="integration-cmdb-section__actions"><button type="button" className="shortcut-button" disabled={loadingCMDBSourceEvidence} onClick={() => void saveCMDBSourceRetention(connectionRecord.id!)}>Save v{cmdbSourceRetention.version + 1}</button></div>
                </div>}
                  </div>
                </details>
              </div>
              <div hidden={cmdbWorkspaceTab !== 'interpretation'} />
              <div hidden={cmdbWorkspaceTab !== 'governance'} className="org-shell__card integration-cmdb-card" style={{ marginBottom: '1rem' }}>
                <div className="integration-cmdb-card__header">
                  <div>
                    <h3 style={{ margin: 0 }}>Reconciliation Governance</h3>
                    <p style={{ margin: '0.35rem 0 0 0', color: 'var(--text-secondary)' }}>
                      Org-level settings for CMDB comparison and reporting. These settings never authorize changes to a provider.
                    </p>
                  </div>
                </div>
                <div className="integration-cmdb-meta" style={{ marginBottom: '0.75rem' }}>
                  <div className="integration-cmdb-meta__item">
                    <span>Current maturity</span>
                    <strong>
                      {cmdbMaturityScore ? `${cmdbMaturityScore.score}/100 · ${formatCMDBMaturityLabel(cmdbMaturityScore.level)}` : 'Not scored yet'}
                    </strong>
                  </div>
                  <div className="integration-cmdb-meta__item">
                    <span>Score source</span>
                    <strong>{cmdbMaturityScore ? cmdbMaturityScore.source.replaceAll('_', ' ') : 'Observed'}</strong>
                  </div>
                  <div className="integration-cmdb-meta__item">
                    <span>Updated</span>
                    <strong>{formatDateTime(cmdbReconciliationAuditEvents[0]?.createdAt ?? null)}</strong>
                  </div>
                  <div className="integration-cmdb-meta__item">
                    <span>Changed by</span>
                    <strong>{formatAuditActor(cmdbReconciliationAuditEvents[0])}</strong>
                  </div>
                </div>
                <details className="integration-cmdb-disclosure">
                  <summary>Legacy report override</summary>
                  <div className="integration-cmdb-disclosure__body integration-cmdb-policy-grid">
                    <label><span>Legacy maturity level</span><select value={cmdbReconciliationForm.manualMaturityLevel} onChange={updateCMDBReconciliationField('manualMaturityLevel')}><option value="">Use REAP's observed score</option><option value="well_maintained">Well maintained</option><option value="hybrid">Hybrid</option><option value="not_maintained">Not maintained</option></select><small>This affects only the legacy shadow report.</small></label>
                    <label><span>Legacy score</span><input type="number" min={0} max={100} value={cmdbReconciliationForm.manualMaturityScore} onChange={updateCMDBReconciliationField('manualMaturityScore')} placeholder="Optional 0–100" /></label>
                  </div>
                </details>
                {cmdbMaturityScore && (
                  <div className="integration-cmdb-stats" style={{ marginTop: '0.75rem' }}>
                    <div className="integration-cmdb-stat">
                      <span className="integration-cmdb-stat__label">Binding coverage</span>
                      <strong className="integration-cmdb-stat__value">{cmdbMaturityScore.bindingCoveragePercent ?? 0}%</strong>
                    </div>
                    <div className="integration-cmdb-stat">
                      <span className="integration-cmdb-stat__label">Freshness</span>
                      <strong className="integration-cmdb-stat__value">{cmdbMaturityScore.freshnessPercent ?? 0}%</strong>
                    </div>
                    <div className="integration-cmdb-stat">
                      <span className="integration-cmdb-stat__label">Drift</span>
                      <strong className="integration-cmdb-stat__value">{cmdbMaturityScore.driftRatePercent ?? 0}%</strong>
                    </div>
                    <div className="integration-cmdb-stat">
                      <span className="integration-cmdb-stat__label">Relation usability</span>
                      <strong className="integration-cmdb-stat__value">{cmdbMaturityScore.relationUsabilityPercent ?? 0}%</strong>
                    </div>
                  </div>
                )}
                {cmdbReconciliationPolicyDirty && (
                  <p className="integration-cmdb-note" style={{ marginTop: '0.75rem' }}>
                    Reconciliation changes are pending. These rules are org-wide and affect discrepancy reporting for every CMDB-enabled connection.
                  </p>
                )}
                <div className="integration-cmdb-section__actions" style={{ marginTop: '0.75rem' }}>
                  <button
                    type="button"
                    className="shortcut-button"
                    disabled={loadingCMDBReconciliationPolicy}
                    onClick={() => {
                      setErrorMessage(null);
                      void saveCMDBReconciliationPolicy().catch((error) => {
                        setErrorMessage(error instanceof Error ? error.message : 'Failed to save CMDB reconciliation policy.');
                      });
                    }}
                  >
                    {loadingCMDBReconciliationPolicy ? 'Saving…' : 'Save Reconciliation Settings'}
                  </button>
                </div>
                <details style={{ marginTop: '0.75rem' }}>
                  <summary style={{ cursor: 'pointer', fontWeight: 600 }}>Reconciliation history · audit log</summary>
                  {loadingCMDBReconciliationAudit ? (
                    <p style={{ margin: '0.75rem 0 0 0', color: 'var(--text-secondary)' }}>Loading audit events…</p>
                  ) : cmdbReconciliationAuditError ? (
                    <p style={{ margin: '0.75rem 0 0 0', color: 'var(--status-danger)' }}>{cmdbReconciliationAuditError}</p>
                  ) : cmdbReconciliationAuditEvents.length === 0 ? (
                    <p style={{ margin: '0.75rem 0 0 0', color: 'var(--text-secondary)' }}>No audit events recorded yet. Earlier changes are not reconstructed.</p>
                  ) : (
                    <div style={{ display: 'grid', gap: '0.5rem', marginTop: '0.75rem' }}>
                      {cmdbReconciliationAuditEvents.slice(0, CMDB_POLICY_HISTORY_PREVIEW_COUNT).map((event) => (
                        <div key={event.id} style={{ border: '1px solid var(--border-subtle)', borderRadius: '0.75rem', padding: '0.75rem' }}>
                          <div style={{ fontWeight: 600 }}>{formatDateTime(event.createdAt)}</div>
                          <div style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>{formatAuditActor(event)} · {formatReconciliationAuditSummary(event)}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </details>
              </div>
              {cmdbWorkspaceTab === 'access' && cmdbWritebackFlow && (
                <>
                  <div className="org-shell__card integration-cmdb-card" style={{ marginBottom: '1rem' }}>
                    <div className="integration-cmdb-card__header">
                      <div>
                        <h3 style={{ margin: 0 }}>CMDB Operating Policy</h3>
                        <p style={{ margin: '0.35rem 0 0 0', color: 'var(--text-secondary)' }}>
                        Define what REAP may do before validating the corresponding CMDB permissions. Report only is the safe default.
                        </p>
                      </div>
                    </div>
                    {!cmdbWritebackPolicy.globalPublicationEnabled && (
                      <div className="ui-alert ui-alert--warning" style={{ marginBottom: '1rem' }}>
                        The global CMDB publication kill switch is off. No external mutation can run, regardless of this connection policy.
                      </div>
                    )}
                    {cmdbWritebackPolicy.globalPublicationEnabled && !cmdbWritebackPolicy.tenantPublicationEnabled && (
                      <div className="ui-alert ui-alert--warning" style={{ marginBottom: '1rem' }}>
                        CMDB publication is disabled for this organization by the server-side tenant kill switch.
                      </div>
                    )}
                    <CMDBOperatingPolicyEditor
                      value={cmdbWritebackForm}
                      providerLabel={currentConnectionProviderLabel}
                      supportedFields={cmdbWritebackPolicy.supportedFields}
                      createMissingSupported={cmdbWritebackPolicy.createMissingSupported}
                      onChange={setCMDBWritebackForm}
                    />
                    <div className="integration-cmdb-meta">
                      <div className="integration-cmdb-meta__item">
                        <span>Last changed</span>
                        <strong>{formatDateTime(cmdbPolicyAuditEvents[0]?.createdAt ?? null)}</strong>
                      </div>
                      <div className="integration-cmdb-meta__item">
                        <span>Changed by</span>
                        <strong>{formatAuditActor(cmdbPolicyAuditEvents[0])}</strong>
                      </div>
                    </div>
                    {cmdbWritebackPolicyDirty && (
                      <p className="integration-cmdb-note" style={{ marginTop: '0.75rem' }}>
                        Policy changes are pending. Writeback actions will save this policy before running.
                      </p>
                    )}
                    <div className="integration-cmdb-section__actions" style={{ marginTop: '0.75rem' }}>
                      <button
                        type="button"
                        className="shortcut-button"
                        disabled={loadingCMDBWritebackPolicy || !connectionRecord}
                        onClick={() => {
                          if (!connectionRecord) {
                            return;
                          }
                          setErrorMessage(null);
                          void saveCMDBWritebackPolicy(connectionRecord).catch((error) => {
                            setErrorMessage(error instanceof Error ? error.message : 'Failed to save CMDB writeback policy.');
                          });
                        }}
                      >
                        {loadingCMDBWritebackPolicy ? 'Saving…' : 'Save Policy'}
                      </button>
                    </div>
                    <details style={{ marginTop: '0.75rem' }}>
                      <summary style={{ cursor: 'pointer', fontWeight: 600 }}>Policy history · audit log</summary>
                      {loadingCMDBPolicyAudit ? (
                        <p style={{ margin: '0.75rem 0 0 0', color: 'var(--text-secondary)' }}>Loading audit events…</p>
                      ) : cmdbPolicyAuditError ? (
                        <p style={{ margin: '0.75rem 0 0 0', color: 'var(--status-danger)' }}>{cmdbPolicyAuditError}</p>
                      ) : cmdbPolicyAuditEvents.length === 0 ? (
                        <p style={{ margin: '0.75rem 0 0 0', color: 'var(--text-secondary)' }}>No audit events recorded yet. Changes made before audit tracking was enabled are not reconstructed.</p>
                      ) : (
                        <div style={{ display: 'grid', gap: '0.5rem', marginTop: '0.75rem' }}>
                          {cmdbPolicyAuditEvents.slice(0, CMDB_POLICY_HISTORY_PREVIEW_COUNT).map((event) => (
                            <div
                              key={event.id}
                              style={{ border: '1px solid var(--border-subtle)', borderRadius: '0.75rem', padding: '0.75rem' }}
                            >
                              <div style={{ fontWeight: 600 }}>{formatDateTime(event.createdAt)}</div>
                              <div style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                                {formatAuditActor(event)} · {formatPolicyAuditSummary(event)}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </details>
                  </div>
                </>
              )}
              {cmdbWorkspaceTab === 'access' && !cmdbWritebackFlow && (
                <div className="org-shell__card integration-cmdb-card" style={{ marginBottom: '1rem' }}>
                  <h3 style={{ marginTop: 0 }}>CMDB Operating Policy</h3>
                  <p style={{ marginBottom: 0, color: 'var(--text-secondary)' }}>
                    This provider supports read-only CMDB synchronization. REAP will report differences and will not create or update CMDB records.
                  </p>
                </div>
              )}
              {cmdbWorkspaceTab === 'access' && connectionRecord?.id && renderCMDBAccessSummary()}
            </>
          )}

          {showConnectionWebhook && (
            <div className="org-shell__card" style={{ padding: '1rem', marginBottom: '1rem' }}>
              <div style={{ marginBottom: '1rem' }}>
                <h3 style={{ margin: 0 }}>Webhook Setup</h3>
                <p style={{ margin: '0.35rem 0 0 0', color: 'var(--text-secondary)' }}>
                  Available after a successful connection test.
                </p>
              </div>
              {!showWebhookSetup && !webhookProvisioning ? (
                <p style={{ color: 'var(--text-secondary)', margin: 0 }}>Run a connection test to generate webhook details.</p>
              ) : (
                <>
                  <label>
                    <span>Webhook registration</span>
                    <input type="text" value={connectionRecord?.config?.webhook?.registrationId ?? 'Not configured yet'} readOnly />
                  </label>
                  <label>
                    <span>Webhook status</span>
                    <input type="text" value={webhookProvisioning?.status ?? connectionRecord?.config?.webhook?.status ?? 'Not configured'} readOnly />
                  </label>
                  {webhookError && (
                    <div className="org-shell__banner" role="alert">
                      {webhookError}
                    </div>
                  )}
                  {webhookProvisioning && (
                    <>
                      <label>
                        <span>Webhook URL</span>
                        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                          <input type="text" value={webhookProvisioning.url} readOnly />
                          <button type="button" className="shortcut-button" onClick={() => void copyToClipboard('Webhook URL', webhookProvisioning.url)}>
                            Copy
                          </button>
                        </div>
                      </label>
                      {webhookProvisioning.method && (
                        <label>
                          <span>HTTP method</span>
                          <input type="text" value={webhookProvisioning.method} readOnly />
                        </label>
                      )}
                      <label>
                        <span>Signing secret</span>
                        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                          <input type={revealWebhookSecret ? 'text' : 'password'} value={webhookProvisioning.signingSecret} readOnly />
                          <button type="button" className="shortcut-button" onClick={() => setRevealWebhookSecret((prev) => !prev)}>
                            {revealWebhookSecret ? 'Hide' : 'Reveal'}
                          </button>
                          <button type="button" className="shortcut-button" onClick={() => void copyToClipboard('Signing secret', webhookProvisioning.signingSecret)}>
                            Copy
                          </button>
                        </div>
                      </label>
                      {webhookProvisioning.secretHeader && (
                        <label>
                          <span>Secret header</span>
                          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                            <input type="text" value={webhookProvisioning.secretHeader} readOnly />
                            <button type="button" className="shortcut-button" onClick={() => void copyToClipboard('Secret header', webhookProvisioning.secretHeader ?? '')}>
                              Copy
                            </button>
                          </div>
                        </label>
                      )}
                      {webhookProvisioning.signatureHeader && (
                        <label>
                          <span>Signature header</span>
                          <input type="text" value={webhookProvisioning.signatureHeader} readOnly />
                        </label>
                      )}
                      {webhookProvisioning.timestampHeader && (
                        <label>
                          <span>Timestamp header</span>
                          <input type="text" value={webhookProvisioning.timestampHeader} readOnly />
                        </label>
                      )}
                      {(webhookProvisioning.setupScript || webhookProvisioning.serviceNowScript) && (
                        <label>
                          <span>{webhookProvisioning.setupScriptLabel ?? 'Setup script'}</span>
                          <textarea value={webhookProvisioning.setupScript ?? webhookProvisioning.serviceNowScript ?? ''} readOnly rows={18} />
                        </label>
                      )}
                      {webhookProvisioning.setupInstructions && (
                        <label>
                          <span>Setup instructions</span>
                          <textarea value={webhookProvisioning.setupInstructions} readOnly rows={5} />
                        </label>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          )}

          {copyFeedback && (
            <div className="org-shell__banner" role="status">
              {copyFeedback}
            </div>
          )}
        </form>
      </Drawer>

      <DeleteConfirmationModal
        open={deleteDialog != null}
        title={deleteDialog?.title ?? ''}
        description={deleteDialog?.description ?? ''}
        deleting={deleteDialogBusy}
        onCancel={closeDeleteDialog}
        onConfirm={() => void confirmDeleteDialog()}
      />

      {deleteToast && (
        <StatusToast tone={deleteToast.tone} message={deleteToast.message} occurrence={deleteToast} onDismiss={() => setDeleteToast(null)} />
      )}

      <Drawer
        open={targetDrawerOpen}
        title={targetDrawerMode === 'create' ? 'New interaction target' : targetRecord?.name ?? 'Interaction target'}
        subtitle="User, channel, and thread bindings for configured conversation providers."
        onClose={() => setTargetDrawerOpen(false)}
        width="wide"
      >
        <form
          className="org-settings-form"
          onSubmit={(event) => {
            event.preventDefault();
            void submitTarget();
          }}
        >
          <label>
            <span>Name</span>
            <input type="text" value={targetForm.name} onChange={updateTargetFormField('name')} />
          </label>
          <label>
            <span>Provider</span>
            <select value={targetForm.provider} onChange={updateTargetFormField('provider')}>
              {interactionProviderDefinitions.map((provider) => (
                <option key={provider.providerKey} value={provider.providerKey}>
                  {provider.displayName}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span>Connection</span>
            <select value={targetForm.connectionId} onChange={updateTargetFormField('connectionId')}>
              <option value="">Select connection</option>
              {interactionConnections
                .filter((item) => item.provider === targetForm.provider)
                .map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name ?? item.id}
                  </option>
                ))}
            </select>
          </label>
          <label>
            <span>Target type</span>
            <select value={targetForm.targetType} onChange={updateTargetFormField('targetType')}>
              <option value="channel">Channel</option>
              <option value="user">User</option>
            </select>
          </label>
          <label>
            <span>External target ID</span>
            <input type="text" value={targetForm.externalTargetId} onChange={updateTargetFormField('externalTargetId')} />
          </label>
          <label>
            <span>Thread binding ID</span>
            <input type="text" value={targetForm.threadBindingId} onChange={updateTargetFormField('threadBindingId')} />
          </label>
          <label>
            <span>Labels</span>
            <input type="text" value={targetForm.labels} onChange={updateTargetFormField('labels')} placeholder="noc, sev1, approvals" />
          </label>
          <label>
            <span>Meta JSON</span>
            <textarea value={targetForm.metaJson} rows={8} onChange={updateTargetFormField('metaJson')} />
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <input type="checkbox" checked={targetForm.isDefault} onChange={updateTargetFormField('isDefault')} />
            <span>Default target</span>
          </label>
          <div style={{ marginTop: '1rem', display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
            {targetDrawerMode === 'detail' && targetRecord?.id && (
              <button type="button" className="shortcut-button" onClick={() => openTargetDeleteDialog(targetRecord)} disabled={rowActionBusy === targetRecord.id}>
                Delete
              </button>
            )}
            <button type="submit" className="shortcut-button" disabled={savingTarget}>
              {savingTarget ? 'Saving…' : targetDrawerMode === 'create' ? 'Create Target' : 'Save Changes'}
            </button>
          </div>
        </form>
      </Drawer>

      <Drawer
        open={ruleDrawerOpen}
        title={ruleDrawerMode === 'create' ? 'New routing rule' : ruleRecord?.name ?? 'Routing rule'}
        subtitle="Provider-agnostic mapping from ticket attributes to interaction targets."
        onClose={() => setRuleDrawerOpen(false)}
        width="wide"
      >
        <form
          className="org-settings-form"
          onSubmit={(event) => {
            event.preventDefault();
            void submitRule();
          }}
        >
          <label>
            <span>Name</span>
            <input type="text" value={ruleForm.name} onChange={updateRuleFormField('name')} />
          </label>
          <label>
            <span>Provider</span>
            <select value={ruleForm.provider} onChange={updateRuleFormField('provider')}>
              {interactionProviderDefinitions.map((provider) => (
                <option key={provider.providerKey} value={provider.providerKey}>
                  {provider.displayName}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span>Connection</span>
            <select value={ruleForm.connectionId} onChange={updateRuleFormField('connectionId')}>
              <option value="">Select connection</option>
              {interactionConnections
                .filter((item) => item.provider === ruleForm.provider)
                .map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name ?? item.id}
                  </option>
                ))}
            </select>
          </label>
          <label>
            <span>Target ref</span>
            <input type="text" value={ruleForm.targetRef} onChange={updateRuleFormField('targetRef')} placeholder="pager-escalation-room" />
          </label>
          <label>
            <span>Assignment group</span>
            <input type="text" value={ruleForm.assignmentGroup} onChange={updateRuleFormField('assignmentGroup')} />
          </label>
          <label>
            <span>Category</span>
            <input type="text" value={ruleForm.category} onChange={updateRuleFormField('category')} />
          </label>
          <label>
            <span>Priority</span>
            <input type="text" value={ruleForm.priority} onChange={updateRuleFormField('priority')} />
          </label>
          <label>
            <span>Target override ref</span>
            <input type="text" value={ruleForm.targetOverrideRef} onChange={updateRuleFormField('targetOverrideRef')} />
          </label>
          <label>
            <span>Conditions JSON</span>
            <textarea value={ruleForm.conditionsJson} rows={8} onChange={updateRuleFormField('conditionsJson')} />
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <input type="checkbox" checked={ruleForm.enabled} onChange={updateRuleFormField('enabled')} />
            <span>Enabled</span>
          </label>
          <div style={{ marginTop: '1rem', display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
            {ruleDrawerMode === 'detail' && ruleRecord?.id && (
              <button type="button" className="shortcut-button" onClick={() => openRuleDeleteDialog(ruleRecord)} disabled={rowActionBusy === ruleRecord.id}>
                Delete
              </button>
            )}
            <button type="submit" className="shortcut-button" disabled={savingRule}>
              {savingRule ? 'Saving…' : ruleDrawerMode === 'create' ? 'Create Rule' : 'Save Changes'}
            </button>
          </div>
        </form>
      </Drawer>
    </OrgModuleLayout>
  );
};

export default IntegrationsPage;
